/**
 * Copyright (c) 2018
 * @summary Add filter according to custom options
 * @author Mangesh Pimprikar,Ankur Vishwakarma,Poonam Banode,Irfan Bagwan
 *
 */
import React, { Component } from 'react';
import { flushSync } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Stack } from '@mui/material';
import cx from 'classnames';
import { findIndex, remove, unionBy, uniq } from 'lodash/array';
import { find, forEach, orderBy, reduce } from 'lodash/collection';
import { cloneDeep, isArray, isEmpty, isEqual, isFunction, isNull, isString, isUndefined, toArray } from 'lodash/lang';
import { assign, get, has } from 'lodash/object';
import moment from 'moment';

import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';

import { EncounterApi, ReactRouteOldMetaDataApi, SitePatientApi, StudyApi } from '../../../../../api';
import LogRowStatusHistoryApi from '../../../../../api/patient/LogRowStatusHistoryApi';
import LogTableApi from '../../../../../api/patient/LogTableApi';
import LogTableEncounterApi from '../../../../../api/patient/LogTableEncounterApi';
import NewEncounterLogCheckApi from '../../../../../api/patient/NewEncounterLogCheckApi';
import UserApiApi from '../../../../../api/patient/UserApiApi';
import UserMediaApi from '../../../../../api/patient/UserMediaApi';
import Common from '../../../../../common/common';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { yearMonthDayFormat } from '../../../../../constants/dateFormat';
import { NON_PROTOCOL, UNEXPECTED } from '../../../../../constants/encounter';
import { FILE_TYPES } from '../../../../../constants/inputTypes';
import { PROCEDURE_TYPES } from '../../../../../constants/item-group';
import {
  CHECKBOX_EXACT_COPY,
  DATA_SAVED,
  SELECT_REQUIRED,
  SELECT_REQUIRED_STIPEND,
  WRONG_URL
} from '../../../../../constants/notificationMessages';
import { CLOSED } from '../../../../../constants/ssuStatuses';
import {
  MANAGE_LINKED_LOGS,
  MANAGE_LOG_DATA,
  MANAGE_PRE_SCREEN_WORK_LIST,
  MANAGE_PROTOCOL_ENCOUNTER_DATA
} from '../../../../../constants/userOperations';
import userRoles, {
  ALLOWED_TO_MANAGE_NON_PROTOCOL_ENCOUNTER_ROLES,
  ALLOWED_TO_MANAGE_PATIENT_ENCOUNTER_ROLES,
  ROLE_CLINICAL_QUALITY_COORDINATOR,
  ROLE_OPERATION_MANAGER,
  ROLE_OPERATIONS_ANALYST,
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_STUDY_MANAGER,
  ROLE_SYSTEM_ADMINISTRATOR
} from '../../../../../constants/userRoles';
import { userHasAccessTo, userHasRole } from '../../../../../services/auth';
import resolveStudyGroupAssignAndGroupAssignGroups from '../../../../../services/groupAssignStudyGroupsSetvice';
import { onRequestError } from '../../../../../services/handlers';
import {
  checkOnInvestigatorFieldIfNeeded,
  filtrateByInvestigatorFieldIfNeed,
  isInvestigatorFieldAvailable,
  isNotNewReviewableLogType,
  isReviewableLogType
} from '../../../../../services/itemGroupQuestionService';
import { findAllUploadedFilesFromAllFileUploadQuestions } from '../../../../../services/itemGroupUserFileService';
import { isCancelRowStatus, resolveSmStatus } from '../../../../../services/logRowStatusService';
import HistoryBlock, { LEGACY_ITEM_GROUP } from '../../../../root/Container/HistoryBlock';
import { generateUrlByKey, withCurrentRoute } from '../../../../root/router';
import { withNavigate, withParams } from '../../../../root/router/legacyComponentCompatability';
import {
  ALLOW_READ,
  ALLOW_SIGN,
  DENY_ALL
} from '../../../setup/Protocol/ProtocolSetup/ProtocolGroupsSetup/permissionType';
import { showEncounterColumn } from '../../../setup/shared/ElementSetupNew/defaultLogCheckOptions';
import { isReadOnlyField } from '../../../setup/shared/ElementSetupNew/itemGroupSetupService';
import { LinkLogButton } from '../../Patients/Logs/LinkLogRecord/LinkLogButton';
import {
  isCurrentPageForLogWithAvailableReviewCycle,
  isLogAvailableForReviewCycle,
  isLogCanBeLinked,
  userCanSignOrEdit
} from '../../Patients/Logs/LogsPageServices';
import PatientStatusChangeModal from '../../Patients/PatientInfo/PatientInfoSidebar/PatientStatusChangeModal';
import ItemGroupBar from '../ItemGroupBar/ItemGroupBar';
import { LOGS } from '../Review/reviewConstants';
import { resolveDomainFullName } from '../Worklist/WorklistService';

import EncounterPatientInfo from './EncounterPatientInfo/EncounterPatientInfo';
import {
  populateIcfAnswers,
  resetIcfMandatoryFields,
  setIcfMandatoryFields
} from './InformedConcent/InformedConsentUtils';
import InvestigatorFieldsSignatureModal from './InvestigatorFieldsSignature/InvestigatorFieldsSignatureModal';
import CertifyCopySignModal from './item-group/CertifyCopySignModal/CertifyCopySignModal';
import { prepareItemGroupTableData } from './item-group/itemGroupService';
import LogRowCloseReopen from './item-group/LogRowCloseReopen';
import LogRecordsNavigation from './LogRowsNavigation/LogRecordsNavigation';
import { MissingItemDialog } from './MissingItemDialog/MissingItemDialog';
import { newDataForSave } from './MissingItemDialog/MissingItemDialogService';
import { PS_ANSWERED, PS_NOT_ANSWERED, PS_UNAVAILABLE } from './TableItemGroup/constants';
import UserFormTable from './UserFormTable/UserFormTable';
import { findQuestionsWithChangedAnswers, isAnyFieldDataUnsaved } from './changeDataService';
import CommentTemplate from './commentTemplate';
import {
  calculateHiddenFieldIds,
  fieldAffectsOnConditionalLogic,
  getHiddenTargetFieldIdsByTriggerFieldValue,
  isShownNotFilledConditionsExist,
  prepareFormValidationRules,
  recalculateVisibility,
  recursivelyCollectVisibleAndHiddenFieldIdsByTriggerField,
  retainOldStatus
} from './conditionalLogicServices.js';
import {
  buildNewFileData,
  generateJsonForSaveByFormDefinition,
  getFormDataRecordByFormGroupIdentifier,
  getInputsByFormDefinitionLabelList,
  getLatestFormDataRecord,
  getSaveJSONInfo,
  getWhenAndWhoDidItByFormGroupIdentifier,
  getWhenAndWhoDidItFromFormDataRecord,
  getWhenAndWhoDidItFromLastFormDataRecord,
  prepareFormDefinitionLabelList,
  prepareFormDefinitionLabelListWithFilledAnswersForQuestions
} from './formDefinitionService';
import ReasonForDataChangeModal from './reasonForDataChangeModal';

import './getDomainDetailElements.scss';

const EDIT_CHECK_DOMAINS = [
  'AE',
  'CE',
  'CM',
  'DA',
  'DV',
  'DE',
  'IE',
  'VS',
  'PR',
  'EC',
  'LB',
  'MH',
  'PE',
  'RP',
  'SU',
  'ICF'
];
const formActions = Object.freeze({ SUBMIT: 'submit', CANCEL: 'cancel' });

class GetDomainDetailElements extends Component {
  whenAndWhoDidItDefault = {
    whenWasItDone: Common.formatDate(moment(), yearMonthDayFormat),
    whoDidItUniqueIdentifier: null
  };

  constructor(props) {
    super(props);
    this.state = {
      metadata: {
        itemGroupTemplateVersionId: null,
        patientEncounterGroupId: null,
        ssuId: null,
        studyId: null,
        encounterType: null
      },
      inputs: {},
      previousInputs: {},
      jsonForSave: [],
      filesToUpload: {},
      filesToUploadGeneralSize: 0,
      originalJsonForSave: [],
      fillDate: null,
      originalFillDate: null,
      whoDidItUniqueIdentifier: null,
      originalWhoDidItUniqueIdentifier: null,
      whoDidItUniqueIdentifierPredefined: false,
      selectedEncounterId: null,
      patientEncounterFormGroupIdentifier: '',
      whoCdashList: [],
      hasTableView: false,
      additionNotes: '',
      originalAdditionNotes: '',
      formValidationRules: '',
      fullResponse: '',
      deleteArray: [],
      allResponseData: [],
      patientInfo: null,
      hasTableType: false,
      editedRowType: '',
      mounted: false,
      originalLabelData: [],
      encounter: {},
      encounterLogPage: this.props.currentRoute.path.includes('/logs/') && has(this.props.params, 'patientEncounterId'),
      patientInfoLogPage:
        (this.props.currentRoute.path.includes('/logs/') ||
          this.props.currentRoute.path.includes('/adverse-event-log/') ||
          this.props.currentRoute.path.includes('/deviation-log/')) &&
        !has(this.props.params, 'patientEncounterId'),
      currentLogRowId: this.props.params.rowId,
      dropStatusDetails: {},
      savedPatientStatus: {},
      checkedInEncountersForPatient: null,
      isRequireAttentionOriginalValues: {},
      validation: {
        isSomeCertifyCopyWasNotConfirmedAfterSaving: false
      },
      isDetailRowOpen: false,
      areFilesAttached: false,
      logRowStatus: null,
      currentUserPermissionType: null,
      groupAssignList: null
    };
    this.height = '';
    this.ref = React.createRef();
    this.blockedNavigation = null;
    this.excludeQues = ['label', 'textBlockLong', 'reminder'];
  }

  componentDidMount() {
    if (this.state.encounterLogPage || this.state.patientInfoLogPage) {
      LogTableEncounterApi.getCheckedInEncountersForPatient(this.props.params.ssuPatientId).then(res => {
        this.setState({
          checkedInEncountersForPatient: res.data.checkedInEncounters,
          selectedEncounterId: this.props.params.patientEncounterId
        });
      });
    }
    if (this.state.patientInfoLogPage) {
      LogTableApi.getPermissionTypeForLog(this.props.params.ssuPatientId, this.props.params.patientItemGroupId).then(
        ({ data }) => {
          this.setState({ currentUserPermissionType: data });
        }
      );
      UserApiApi.getListOfActiveUsers(
        this.props.params.ssuPatientId,
        this.props.params.patientItemGroupId,
        this.props.params.rowId
      )
        .then(res => {
          this.setState({
            whoCdashList: res.data.response
          });
        })
        .then(this.loadFormDataSetGeneral);
    } else {
      ReactRouteOldMetaDataApi.getRouteOldMetadataForItemGroupPage(
        this.props.params.patientEncounterId,
        this.props.params.patientItemGroupId
      ).then(({ data }) => {
        const { patientEncounterGroupId, itemGroupTemplateVersionId, ssuId, encounterType, studyId } = data;
        this.setState(
          {
            metadata: {
              itemGroupTemplateVersionId,
              patientEncounterGroupId,
              ssuId,
              studyId,
              encounterType
            }
          },
          () => {
            UserApiApi.getListOfActiveUsers(
              this.props.params.ssuPatientId,
              this.props.params.patientItemGroupId,
              this.props.params.rowId
            )
              .then(res => {
                this.setState({
                  whoCdashList: res.data.response
                });
              })
              .then(this.loadFormDataSetGeneral)
              .then(() => {
                isEmpty(this.state.previousInputs) && this.setState({ previousInputs: cloneDeep(this.state.inputs) });
              });
          }
        );
      });
    }
    this.blockedNavigation = HistoryBlock.block(LEGACY_ITEM_GROUP, discard => {
      if (!this.state.hasTableView && this.isUnsavedData() && this.state.encounter.domainCode !== 'CO') {
        this.props?.currentRoute?.path.includes('encounters')
          ? this.onSaveData(() => {
              discard();
            })
          : ModalBoxes.confirm({
              content: <div>Are you sure you want to leave this page without saving your edits?</div>,
              confirmButton: 'Yes',
              cancelButton: 'No'
            }).then(
              () => {
                discard();
              },
              () => {}
            );
        return false;
      }
      return true;
    });
  }

  componentWillUnmount() {
    if (this.blockedNavigation) {
      this.blockedNavigation();
    }
    this.setState({});
  }

  isUnsavedData() {
    const {
      fillDate,
      originalFillDate,
      whoDidItUniqueIdentifier,
      originalWhoDidItUniqueIdentifier,
      whoDidItUniqueIdentifierPredefined,
      additionNotes,
      originalAdditionNotes,
      inputs,
      originalLabelData,
      isRequireAttentionOriginalValues,
      jsonForSave
    } = this.state;

    // isRequireAttentionOriginalValues dynamically keeps track of the original values whenever a change
    // to the toggles for requiring attention have been changed. This might be empty if they were never
    // toggled. jsonForSave contains the newest possibly unsaved isRequireAttention data. This will check
    // if there are any differences.
    const isRequireAttentionChanged = Object.entries(isRequireAttentionOriginalValues).some(keyValue => {
      const key = keyValue[0];
      const value = keyValue[1];

      const newValue = jsonForSave?.find(i => i.elementEncounterIdentifier === key)?.isRequireAttention;

      // by default all isRequireAttention are nulls
      // to begin with until they are saved as either false or true
      // We make sure to turn nulls into a falses before comparing
      return !!value !== !!newValue;
    });

    // Creating an array of item pairs
    // First item in pair is value from page,
    // and second item in pair is last known saved value
    // If any pair is not equal then there is unsaved
    // data.
    const nonFormFieldDataPairsToCompare = [
      [fillDate, originalFillDate],
      whoDidItUniqueIdentifierPredefined ? [] : [whoDidItUniqueIdentifier, originalWhoDidItUniqueIdentifier],
      [additionNotes, originalAdditionNotes]
    ];

    const labelList = inputs?.labelList || [];

    // First check if isRequireAttentionChanged ||
    // if not check if any of the nonFormFielDataPairs has unsaved data ||
    // if not then check the rest of the form field data.
    return (
      isRequireAttentionChanged ||
      nonFormFieldDataPairsToCompare.some(pair => pair[0] !== pair[1]) ||
      isAnyFieldDataUnsaved(labelList, originalLabelData)
    );
  }

  /**
   * The content of this method should be used somewhere in loadFormDataSet when inputs and jsonForSave are set.
   */
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      !this.state.mounted &&
      !this.state.jsonForSave.some(e => {
        return e.hasOwnProperty('show');
      }) &&
      Object.keys(this.state.inputs).length
    ) {
      this.addShowAndConditionalFlags();
    }

    if (!isString(this.state.fillDate)) {
      this.setState({
        fillDate: this.whenAndWhoDidItDefault.whenWasItDone,
        originalFillDate: this.whenAndWhoDidItDefault.whenWasItDone
      });
    }

    if (prevState.filesToUpload !== this.state.filesToUpload) {
      this.setState({
        filesToUploadGeneralSize: reduce(
          this.state.filesToUpload,
          (acc, group) => {
            return acc + reduce(group, (acc, file) => acc + file.size, 0);
          },
          0
        )
      });
    }
  }

  addShowAndConditionalFlags() {
    const jsonForSave = this.state.jsonForSave;
    const formValidationRules = this.state.formValidationRules;
    this.fillWithAnEmptyAnswers(jsonForSave);

    const hiddenFieldIds = calculateHiddenFieldIds(jsonForSave, formValidationRules);

    jsonForSave.forEach(formField => {
      const { elementEncounterIdentifier } = formField;
      formField.show = elementEncounterIdentifier !== 'COVAL' && !hiddenFieldIds.includes(elementEncounterIdentifier);
    });
    this.setState({ jsonForSave, mounted: true });
  }

  loadFormDataSet = () => {
    return EncounterApi.getDynamicEncounterForm({
      ssuPatientId: get(this, 'props.params.ssuPatientId'),
      uniqueIdentifier: get(this, 'state.metadata.itemGroupTemplateVersionId'),
      elementIdentifier: get(this, 'props.params.patientItemGroupId')
    })
      .then(res => {
        const forthcomingState = {
          patientInfo: has(res, 'data.userInfoResponse') && cloneDeep(res.data.userInfoResponse),
          allResponseData: JSON.stringify(res.data.response)
        };

        const form = get(res, 'data.response.form');
        const isCommentsItemGroup = has(form, 'listOfComments');
        const dataSet = this.getCurrentItemDefinition(res.data.response);
        const currentFormData = dataSet.formData;
        const currentFormDefinition = dataSet.formDefinition;
        const elementType = get(currentFormDefinition, 'elementIcon');
        const isProcedureRepresentation = !isCommentsItemGroup && this.isProcedureRepresentation(elementType);
        const isLogsType = currentFormDefinition.elementIcon === 'Logs';
        const { whenDidIt, whoDidItUniqueIdentifier, additionNotes } = getWhenAndWhoDidItFromLastFormDataRecord(
          currentFormData
        );
        if (isLogsType) {
          NotificationManager.error(WRONG_URL);
          return;
        }
        if (isCommentsItemGroup) {
          assign(forthcomingState, {
            hasTableView: true
          });
        } else {
          const formDataNotEmpty = !isEmpty(currentFormData);

          assign(forthcomingState, {
            fullResponse: dataSet
          });

          if (!isEmpty(currentFormDefinition.labelList)) {
            const latestFormDataRecord = getLatestFormDataRecord(currentFormData);

            assign(forthcomingState, {
              formValidationRules: prepareFormValidationRules(dataSet),
              hasTableView: !isProcedureRepresentation
            });

            if (currentFormDefinition.elementIcon && currentFormDefinition.elementIcon === 'Table') {
              assign(forthcomingState, { hasTableType: true });
            }

            currentFormDefinition.labelList = prepareFormDefinitionLabelList(currentFormDefinition.labelList);

            if (formDataNotEmpty && isProcedureRepresentation && currentFormDefinition) {
              const currentFileList = dataSet.fileList,
                currentLabelList = currentFormDefinition.labelList,
                labelList = prepareFormDefinitionLabelListWithFilledAnswersForQuestions(
                  currentLabelList,
                  latestFormDataRecord,
                  currentFileList
                );
              if (!isEmpty(labelList)) {
                currentFormDefinition.labelList = labelList;
              }
            }

            if (formDataNotEmpty && currentFormDefinition && dataSet?.fileList?.length) {
              assign(forthcomingState, {
                areFilesAttached: true
              });
            }

            if (formDataNotEmpty && isProcedureRepresentation) {
              assign(forthcomingState, {
                patientEncounterFormGroupIdentifier: latestFormDataRecord.fromGroupIdentifier,
                jsonForSave: toArray(latestFormDataRecord.pateintEncounterFieldResponse)
              });
            }

            if (!formDataNotEmpty) {
              assign(forthcomingState, {
                jsonForSave: generateJsonForSaveByFormDefinition(
                  currentFormDefinition.labelList,
                  currentFormDefinition.columnList
                )
              });
            }
          }
        }

        if (!isCommentsItemGroup && isProcedureRepresentation) {
          if (additionNotes) {
            assign(forthcomingState, {
              additionNotes
            });
          }
          assign(forthcomingState, {
            originalAdditionNotes: additionNotes || ''
          });
          if (!isEmpty(whenDidIt)) {
            assign(forthcomingState, {
              fillDate: whenDidIt,
              originalFillDate: whenDidIt
            });
          }

          if (!isEmpty(whoDidItUniqueIdentifier)) {
            assign(forthcomingState, {
              whoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
              originalWhoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
              whoDidItUniqueIdentifierPredefined: false
            });
          }
        }

        const inputs = isCommentsItemGroup ? form : currentFormDefinition;
        assign(forthcomingState, {
          inputs,
          originalLabelData: getInputsByFormDefinitionLabelList(inputs.labelList),
          encounter: dataSet.encounter
        });
        assign(forthcomingState, {
          validation: {
            ...this.state.validation,
            isSomeCertifyCopyWasNotConfirmedAfterSaving: false
          }
        });
        flushSync(() => {
          this.setState(forthcomingState);
        });
      })
      .then(() => this.getSavedPatientStatus());
  };

  getLogRow = params => {
    if (params.rowId === 'newRow') {
      return LogTableApi.getActualLogFormFromPatientInfo(params);
    } else {
      return LogTableApi.getLogRow(params);
    }
  };

  isProcedureRepresentation = type => {
    return PROCEDURE_TYPES.includes(type);
  };

  loadLogRowLastStatus = (studyId, ssuPatientId, itemGroupTemplateId, itemGroupTemplateVersionId, rowId) => {
    LogRowStatusHistoryApi.getLogRowLastStatus(
      studyId,
      ssuPatientId,
      itemGroupTemplateId,
      itemGroupTemplateVersionId,
      rowId
    ).then(({ data }) => {
      this.setState({
        logRowStatus: data
      });
    });
  };

  loadFormDataSetForLog = () => {
    const params = {
      studyId: this.state.metadata.studyId,
      ssuPatient: this.props.params.ssuPatientId,
      encounterId: this.props.params.patientEncounterId,
      itemGroupTemplateVersionId:
        this.state.patientInfoLogPage || this.state.encounterLogPage
          ? this.props.params.itemGroupTemplateVersionId
          : this.state.metadata.itemGroupTemplateVersionId,
      rowId: this.state.currentLogRowId
    };

    return this.getLogRow(params).then(res => {
      const forthcomingState = {
        patientInfo: has(res, 'data.userInfoResponse') && cloneDeep(res.data.userInfoResponse)
      };

      const isCommentsItemGroup = false;
      const preparedData1 = {};

      if (
        this.state.currentLogRowId !== 'newRow' &&
        isReviewableLogType(res.data.formDefinition.domainCode, res.data.formDefinition.elementIcon) &&
        userHasRole([
          ROLE_STUDY_MANAGER,
          ROLE_PRINCIPAL_INVESTIGATOR,
          ROLE_SYSTEM_ADMINISTRATOR,
          ROLE_OPERATION_MANAGER,
          ROLE_OPERATIONS_ANALYST,
          ROLE_CLINICAL_QUALITY_COORDINATOR
        ])
      ) {
        this.loadLogRowLastStatus(
          this.state.metadata.studyId || this.state.studyId || res.data.studyId,
          this.props.params.ssuPatientId,
          res.data.formDefinition.itemGroupKey,
          this.props.params.itemGroupTemplateVersionId,
          this.state.currentLogRowId
        );
      }

      if (res.data.formData && res.data.formData.answers) {
        res.data.formData.answers
          .map(a => {
            a.attributeType = a.attributeType || '';
            if (this.isMultiselectInputType(a.attributeType)) {
              if (a.attributeValue.indexOf('$$###$$') > 0) {
                const list = a.attributeValue.split('$$###$$');
                a.originalValue = a.attributeValue;
                a.attributeValue = JSON.stringify(list.slice(0, list.length - 1));
              } else {
                a.attributeValue = '';
              }
            }
            return a;
          })
          .forEach(e => {
            preparedData1[e.elementEncounterIdentifier] = e;
          });
      }

      const currentFormData = { pateintEncounterFieldResponse: preparedData1 };
      const currentFormDefinition = res.data.formDefinition;
      let labelList = currentFormDefinition.labelList;
      const { additionNotes } = getWhenAndWhoDidItFromLastFormDataRecord([currentFormData]);
      const whenDidIt = get(res, 'data.formData.whenDiditInCdash');
      const whoDidItUniqueIdentifier = get(res, 'data.formData.whoDiditInCdash');
      const selectedEncounterId = get(res, 'data.formData.associatedEncounterId');

      //TODO: check this out

      if (isCommentsItemGroup) {
        assign(forthcomingState, {
          hasTableView: true
        });
      } else {
        if (this.state.encounterLogPage) {
          LogTableApi.getPermissionTypeForLog(this.props.params.ssuPatientId, currentFormDefinition?.itemGroupKey).then(
            ({ data }) => {
              this.setState({ currentUserPermissionType: data });
            }
          );
        }

        const formDataNotEmpty = !isEmpty(res?.data?.formData?.answers);
        if (!isEmpty(labelList)) {
          assign(forthcomingState, {
            formValidationRules: prepareFormValidationRules(res.data),
            hasTableView: false
          });

          if (currentFormDefinition.elementIcon && currentFormDefinition.elementIcon === 'Table') {
            assign(forthcomingState, { hasTableType: true });
          }

          labelList = prepareFormDefinitionLabelList(labelList);

          if (currentFormData) {
            const currentFileList = res.data.formData?.fileList;
            labelList = prepareFormDefinitionLabelListWithFilledAnswersForQuestions(
              labelList,
              currentFormData,
              currentFileList
            );
          }

          let anyAdverseEventLabel = labelList.find(label => label.cdashAliasName === 'AEYN');
          const isNewAnyAdverseEventLabel =
            isReviewableLogType(res.data.formDefinition.domainCode, res.data.formDefinition.elementIcon) &&
            anyAdverseEventLabel &&
            !formDataNotEmpty;
          if (isNewAnyAdverseEventLabel) {
            assign(anyAdverseEventLabel, {
              inputValue: 'Y',
              ansStatus: PS_ANSWERED
            });
          }

          if (res.data.formData && res.data.formData.answers) {
            const questionToInvestigatorField = new Map(labelList?.map(e => [e.uniqueIdentifier, e.investigatorField]));
            const answers = res.data.formData.answers.map(a => {
              a.attributeType = a.attributeType || '';
              a.investigatorField = questionToInvestigatorField.get(a.elementEncounterIdentifier);
              return a;
            });
            assign(forthcomingState, {
              patientEncounterFormGroupIdentifier: res.data.formData.formGroupIdentifier,
              jsonForSave: answers,
              originalJsonForSave: cloneDeep(answers)
            });
          }

          assign(forthcomingState, {
            formDomainMasterUniqueIdentifier: res.data.formDefinition.uniqueIdentifier
          });

          assign(forthcomingState, {
            studyId: res.data.studyId
          });
          if (!formDataNotEmpty) {
            const answers = generateJsonForSaveByFormDefinition(labelList, []);
            assign(forthcomingState, {
              studyId: res.data.studyId,
              jsonForSave: answers,
              originalJsonForSave: cloneDeep(answers)
            });
          }
        }
      }

      if (!isCommentsItemGroup) {
        if (additionNotes) {
          assign(forthcomingState, {
            additionNotes,
            originalAdditionNotes: additionNotes
          });
        }
        assign(forthcomingState, {
          originalAdditionNotes: additionNotes || ''
        });

        if (!isEmpty(whenDidIt)) {
          assign(forthcomingState, {
            fillDate: whenDidIt,
            originalFillDate: whenDidIt
          });
        }

        if (!isEmpty(whoDidItUniqueIdentifier)) {
          assign(forthcomingState, {
            whoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
            originalWhoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
            whoDidItUniqueIdentifierPredefined: false
          });
        }
        if (res.data.formData) {
          assign(forthcomingState, {
            selectedEncounterId
          });
        }

        if (res.data.formData?.fileList.length) {
          assign(forthcomingState, {
            areFilesAttached: true
          });
        }
      }

      const inputs = res.data.formDefinition;
      inputs.columnList = [];
      inputs.labelList = labelList;

      assign(forthcomingState, {
        inputs,
        originalLabelData: getInputsByFormDefinitionLabelList(inputs.labelList),
        fullResponse: res.data
      });

      resolveStudyGroupAssignAndGroupAssignGroups(res.data.studyId).then(res => {
        this.setState({
          groupAssignList: res.groups
        });
      });
      flushSync(() => {
        this.setState(forthcomingState);
      });
    });
  };

  loadFormDataSetGeneral = () => {
    if (this.state.encounterLogPage || this.state.patientInfoLogPage) {
      return this.loadFormDataSetForLog();
    } else {
      return this.loadFormDataSet();
    }
  };

  onChangeTextHandler = (e, formLabelId, oid, questionData) => {
    const prevSt = this.state.inputs;
    const qIndex = findIndex(prevSt.labelList, o => {
      return questionData.uniqueIdentifier === o.uniqueIdentifier;
    });
    if (qIndex > -1) {
      const uid = prevSt.labelList[qIndex].uniqueIdentifier;
      prevSt.labelList[qIndex].inputValue = e.target.value;
      // check for questions ans has  alrady giver
      if (prevSt.labelList[qIndex].hasOwnProperty('ansStatus') && e.target.value.trim() === '') {
        prevSt.labelList[qIndex].ansStatus = PS_NOT_ANSWERED;
      }
      // setting question answers
      const prevSaveJson = this.state.jsonForSave;
      const checkForEle = findIndex(prevSaveJson, r => {
        return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
      });
      if (checkForEle > -1) {
        prevSaveJson[checkForEle].attributeValue = e.target.value;
        prevSaveJson[checkForEle].comments = '';
        prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
        prevSaveJson[checkForEle].attributeType = 'field';
      } else {
        const qinfo = getSaveJSONInfo();
        qinfo.attributeValue = e.target.value;
        qinfo.performedStatus = PS_ANSWERED;
        qinfo.answerStatus = e.target.value.trim() ? 1 : 0;
        qinfo.elementEncounterIdentifier = uid;
        qinfo.comments = '';
        qinfo.tableview = questionData.isTable ? 1 : 0;
        qinfo.attributeType = 'field';
        prevSaveJson.push(qinfo);
      }
      this.setState({
        jsonForSave: prevSaveJson,
        inputs: prevSt
      });
    }
  };

  onChangeDropDownHandler = (options, formLabelId, oid, questionData) => {
    try {
      const prevSt = this.state.inputs;
      let hasMesurement = false;
      const qIndex = findIndex(prevSt.labelList, o => {
        return questionData.uniqueIdentifier === o.uniqueIdentifier;
      });
      if (qIndex > -1) {
        const uid = prevSt.labelList[qIndex].uniqueIdentifier;
        let selOptValue = '';
        // for multiselect
        if (
          prevSt.labelList[qIndex].hasOwnProperty('codeDefinationList') &&
          prevSt.labelList[qIndex].codeDefinationList.length > 0 &&
          prevSt.labelList[qIndex].codeDefinationList[0].inputType === 'multiselect'
        ) {
          let mltOpt = '';
          const tmpAr = [];
          options &&
            options.length &&
            options.forEach(mobj => {
              mltOpt += `${mobj.decode || mobj.name ? mobj.decode || mobj.name : ''}$$###$$`;
              tmpAr.push(mobj.decode || mobj.name ? mobj.decode || mobj.name : '');
            });
          selOptValue = mltOpt;
          prevSt.labelList[qIndex].inputValue = tmpAr && tmpAr.length ? JSON.stringify(tmpAr) : '';
        }
        // for singleselect drop down
        if (
          prevSt.labelList[qIndex].hasOwnProperty('codeDefinationList') &&
          prevSt.labelList[qIndex].codeDefinationList.length > 0 &&
          prevSt.labelList[qIndex].codeDefinationList[0].inputType === 'dropdown'
        ) {
          selOptValue = options && (options.decode || options.name) ? options.decode || options.name : '';
          prevSt.labelList[qIndex].inputValue = selOptValue;
        }
        if (
          prevSt.labelList[qIndex].hasOwnProperty('measurementDefinitionList') &&
          prevSt.labelList[qIndex].measurementDefinitionList.length > 0
        ) {
          prevSt.labelList[qIndex].inputValue =
            options && (options.decode || options.name) ? options.decode || options.name : '';
          hasMesurement = true;
        }
        if (prevSt.labelList[qIndex]['inputValue'] === '' || prevSt.labelList[qIndex]['inputValue'].length === 0) {
          prevSt.labelList[qIndex]['ansStatus'] = PS_NOT_ANSWERED;
        }
        // setting question answers
        const prevSaveJson = this.state.jsonForSave;
        const checkForEle = findIndex(prevSaveJson, r => {
          return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
        });
        if (checkForEle > -1) {
          prevSaveJson[checkForEle].attributeValue = !hasMesurement ? selOptValue : '';
          prevSaveJson[checkForEle].comments = '';
          prevSaveJson[checkForEle].meausrementvalue = hasMesurement ? options && (options.decode || options.name) : '';
          prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
          prevSaveJson[checkForEle].attributeType = 'field';
          // ED-503 for show the AE/MH value

          if (
            options &&
            questionData &&
            questionData.hasOwnProperty('codeDefinationList') &&
            questionData.codeDefinationList.length
          ) {
            const foundTerm = questionData.codeDefinationList.filter(d => {
              return d.name === options.name;
            });
            if (foundTerm.length && foundTerm[0].hasOwnProperty('term') && foundTerm[0].hasOwnProperty('linkedRowId')) {
              prevSaveJson[checkForEle].linkedRowId = foundTerm[0].linkedRowId;
              prevSaveJson[checkForEle].term = foundTerm[0].term;
            }
          }
        } else {
          const qinfo = getSaveJSONInfo();
          qinfo.attributeValue = !hasMesurement ? selOptValue : '';
          qinfo.performedStatus = PS_ANSWERED;
          qinfo.answerStatus = 1;
          qinfo.elementEncounterIdentifier = uid;
          qinfo.comments = '';
          qinfo.meausrementvalue = hasMesurement ? options.decode || options.name : '';
          qinfo.tableview = questionData.isTable ? 1 : 0;
          qinfo.attributeType = 'field';
          if (
            options &&
            questionData &&
            questionData.hasOwnProperty('codeDefinationList') &&
            questionData.codeDefinationList.length
          ) {
            const foundTerm = questionData.codeDefinationList.filter(d => {
              return d.name === options.name;
            });
            if (foundTerm.length && foundTerm[0].hasOwnProperty('term') && foundTerm[0].hasOwnProperty('linkedRowId')) {
              qinfo.linkedRowId = foundTerm[0].linkedRowId;
              qinfo.term = foundTerm[0].term;
            }
          }
          prevSaveJson.push(qinfo);
        }
        this.setState(
          {
            jsonForSave: prevSaveJson,
            inputs: prevSt
          },
          () => {
            // validate for the Edit check
            this.resetConditionalQuestion(questionData.uniqueIdentifier, !hasMesurement ? selOptValue : '');
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

  onDateChangeHandler = (e, formLabelId, oid, questionData) => {
    let formattedDate;
    if (e && e.formattedDate) {
      formattedDate = e.formattedDate;
    } else {
      formattedDate = e ? Common.formatDate(e._d, yearMonthDayFormat) : '';
    }
    try {
      const prevSt = this.state.inputs;
      const qIndex = findIndex(prevSt.labelList, o => {
        return questionData.uniqueIdentifier === o.uniqueIdentifier;
      });
      if (prevSt.labelList[qIndex].inputValue === formattedDate) {
        return;
      }
      if (qIndex > -1) {
        const uid = prevSt.labelList[qIndex].uniqueIdentifier;
        prevSt.labelList[qIndex].inputValue = formattedDate;
        if (prevSt.labelList[qIndex]['inputValue'] === '') {
          prevSt.labelList[qIndex]['ansStatus'] = PS_NOT_ANSWERED;
        }
        // setting question answers
        const prevSaveJson = this.state.jsonForSave;
        const checkForEle = findIndex(prevSaveJson, r => {
          return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
        });
        if (checkForEle > -1) {
          prevSaveJson[checkForEle].attributeValue = formattedDate;
          prevSaveJson[checkForEle].comments = '';
          prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
          prevSaveJson[checkForEle].attributeType = 'field';
        } else {
          const qinfo = getSaveJSONInfo();
          qinfo.attributeValue = formattedDate;
          qinfo.performedStatus = PS_ANSWERED;
          qinfo.answerStatus = 1;
          qinfo.elementEncounterIdentifier = uid;
          qinfo.comments = '';
          qinfo.tableview = questionData.isTable ? 1 : 0;
          qinfo.attributeType = 'field';
          prevSaveJson.push(qinfo);
        }
        this.setState({ jsonForSave: prevSaveJson, inputs: prevSt });
      }
    } catch (e) {
      console.log(e);
    }
  };

  deleteFile = (labelUniqueIdentifier, sitePatientIdentifier, fileUniqueIdentifier) => {
    ModalBoxes.confirm({
      content: 'Are you sure you want to delete this file?',
      confirmButton: 'Yes',
      cancelButton: 'No'
    }).then(
      () => {
        this.deleteFileForSingleFileUpload(labelUniqueIdentifier, sitePatientIdentifier, fileUniqueIdentifier);
      },
      () => {}
    );
  };

  deleteFileForSingleFileUpload = (labelUniqueIdentifier, sitePatientIdentifier, fileUniqueIdentifier) => {
    const prevSt = this.state.inputs;
    const newSt = cloneDeep(prevSt);
    newSt.labelList.forEach(label => {
      const { uniqueIdentifier, fileList } = label;
      if (uniqueIdentifier === labelUniqueIdentifier) {
        const fileWasDeletedFromList = !isEqual(
          fileList,
          remove(fileList, ({ uniqueIdentifier }) => uniqueIdentifier === fileUniqueIdentifier)
        );
        if (fileWasDeletedFromList) {
          if (
            this.state.encounter.domainCode === 'ICF' &&
            this.props.informedConsentForm?.id &&
            fileList?.length === 0
          ) {
            resetIcfMandatoryFields(newSt.labelList, this.state.jsonForSave, label);
          }
          this.setState(({ deleteArray }) => ({
            inputs: newSt,
            previousInputs: cloneDeep(prevSt),
            deleteArray: uniq([...deleteArray, { sitePatientIdentifier, fileUniqueIdentifier }])
          }));
        }
      }
    });
  };

  // for file change handeler
  onChangeFileHandler = (files, questionData) => {
    const prevSt = this.state.inputs;
    const qIndex = findIndex(prevSt.labelList, o => {
      return questionData.uniqueIdentifier === o.uniqueIdentifier;
    });
    if (qIndex > -1) {
      prevSt.labelList[qIndex].inputValue = files.map(f => f.originalFile.name).join();
      const uid = prevSt.labelList[qIndex].uniqueIdentifier;
      let prevSaveJson = this.state.jsonForSave;
      prevSaveJson = prevSaveJson.map(prevSaveJsonElement => {
        if (
          FILE_TYPES.includes(prevSaveJsonElement.attributeType) &&
          prevSaveJsonElement.elementEncounterIdentifier === questionData.uniqueIdentifier
        ) {
          prevSaveJsonElement.fileList = [];
        }
        return prevSaveJsonElement;
      });

      const filesToUpload = {};
      files.forEach(file => {
        const fileId = crypto.randomUUID();
        filesToUpload[fileId] = file.originalFile;
        this.setupFileReader(fileId, file, uid, questionData.isTable, prevSaveJson, prevSt, qIndex);
      });
      this.setState(prevState => ({
        filesToUpload: {
          ...prevState.filesToUpload,
          [uid]: filesToUpload
        }
      }));
    }
    this.setState({
      validation: {
        ...this.state.validation,
        isSomeCertifyCopyWasNotConfirmedAfterSaving: false
      }
    });
  };

  setupFileReader = (fileId, file, elementEncounterIdentifier, isTableView, prevSaveJson, prevSt, qIndex) => {
    const self = this;
    const index = findIndex(prevSaveJson, r => {
      return r.elementEncounterIdentifier === elementEncounterIdentifier;
    });
    const reader = new FileReader();
    reader.onload = function(e) {
      if (index > -1) {
        if (!isArray(prevSaveJson[index].fileList)) {
          prevSaveJson[index].fileList = [];
          prevSt.labelList[qIndex].fileList = [];
        }
        prevSaveJson[index].fileList.push(buildNewFileData(fileId, file));
        prevSt.labelList[qIndex].fileList = mergeFileLists(
          prevSt.labelList[qIndex].fileList,
          prevSaveJson[index].fileList
        );
        prevSt.labelList[qIndex].inputValue = file?.originalFile?.name;
        prevSaveJson[index].attributeValue = file?.originalFile?.name;
        prevSaveJson[index].attributeType = 'file';
        prevSaveJson[index].performedStatus = PS_ANSWERED;
        prevSaveJson[index].answerStatus = 1;
        prevSaveJson[index].comments = '';
      } else {
        const qInfo = getSaveJSONInfo();
        qInfo.fileList.push(buildNewFileData(fileId, file));
        qInfo.attributeType = 'file';
        qInfo.attributeValue = file?.originalFile?.name;
        qInfo.performedStatus = PS_ANSWERED;
        qInfo.answerStatus = 1;
        qInfo.elementEncounterIdentifier = elementEncounterIdentifier;
        qInfo.comments = '';
        qInfo.tableview = isTableView ? 1 : 0;
        prevSaveJson.push(qInfo);
      }
      if (self.state.encounter.domainCode === 'ICF' && self.props.informedConsentForm?.id) {
        setIcfMandatoryFields(self.state.inputs.labelList, prevSaveJson, self.props.informedConsentForm);
      }

      self.setState({ jsonForSave: prevSaveJson, inputs: prevSt });
    };
    reader.readAsDataURL(file.originalFile);
  };

  onCloseSelectedFile = (questionData, fileName, isLast) => {
    const prevSt = this.state.inputs;
    const qIndex = findIndex(prevSt.labelList, o => {
      return questionData.uniqueIdentifier === o.uniqueIdentifier;
    });
    const prevSaveJson = this.state.jsonForSave;
    const fileIdsToDelete = prevSt.labelList[qIndex].fileList.filter(e => e.name === fileName).map(e => e.fileId);
    if (qIndex > -1) {
      const labelInputValue = prevSt.labelList[qIndex].inputValue;
      prevSt.labelList[qIndex].inputValue = labelInputValue.replace(fileName, '');
      prevSt.labelList[qIndex].fileList = prevSt.labelList[qIndex].fileList.filter(e => e.name !== fileName);
      const checkForEle = findIndex(prevSaveJson, r => {
        return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
      });
      if (checkForEle > -1) {
        const fileList = prevSaveJson[checkForEle].fileList;
        prevSaveJson[checkForEle].fileList = fileList?.filter(f => f.name !== fileName);
        if (isLast) {
          prevSaveJson[checkForEle].attributeValue = '';
          prevSaveJson[checkForEle].attributeType = '';
          prevSaveJson[checkForEle].extraAttributeValues = {};
          prevSaveJson[checkForEle].multiValues = [];
        }
      }
    }

    if (
      this.state.encounter.domainCode === 'ICF' &&
      this.props.informedConsentForm?.id &&
      prevSt.labelList[qIndex].fileList?.length === 0
    ) {
      resetIcfMandatoryFields(this.state.inputs.labelList, prevSaveJson, questionData);
    }

    this.setState({ jsonForSave: prevSaveJson, inputs: prevSt });
    this.removeFromFilesToUpload(questionData, fileIdsToDelete);
  };

  removeFromFilesToUpload = (questionData, fileIdsToDelete) => {
    this.setState(prevState => {
      const files = prevState.filesToUpload[questionData.uniqueIdentifier];
      if (!files) {
        return { filesToUpload: prevState.filesToUpload };
      }

      const updatedFiles = Object.fromEntries(
        Object.entries(files).filter(([fileId]) => !fileIdsToDelete.includes(fileId))
      );

      if (Object.keys(updatedFiles).length === 0) {
        const updated = { ...prevState.filesToUpload };
        delete updated[questionData.uniqueIdentifier];
        return { filesToUpload: updated };
      }

      return {
        filesToUpload: {
          ...prevState.filesToUpload,
          [questionData.uniqueIdentifier]: updatedFiles
        }
      };
    });
  };

  /**
   * radioVal = 1 -> 'Yes'
   * radioVal = 2 -> 'No'
   * radioVal = 0 -> Undefined
   */
  onChangeRadioHandler = (radioVal, questionData, decodeVal) => {
    try {
      const { areFilesAttached, inputs, formValidationRules } = this.state;
      const hiddenQuestions = getHiddenTargetFieldIdsByTriggerFieldValue(
        formValidationRules,
        questionData.uniqueIdentifier,
        decodeVal
      );
      const fileUploadQuestions = inputs.labelList.filter(
        label => hiddenQuestions.includes(label.uniqueIdentifier) && label.fileList?.length
      );
      if (areFilesAttached && fileUploadQuestions.length) {
        this.confirmUploadedFileRemoval(fileUploadQuestions, radioVal, questionData, decodeVal);
      } else {
        this.finalizeProcessingRadioBtnSelection(radioVal, questionData, decodeVal);
      }
    } catch (e) {
      console.log(e);
    }
  };

  confirmUploadedFileRemoval = (fileUploadQuestions, radioVal, questionData, decodeVal) => {
    const { inputs } = this.state;
    const newSt = cloneDeep(inputs);
    const qIndex = findIndex(newSt.labelList, o => {
      return questionData.uniqueIdentifier === o.uniqueIdentifier;
    });
    const toggleName = newSt.labelList[qIndex].question;
    const fileUploadFieldName = fileUploadQuestions[0].name;
    return ModalBoxes.confirm({
      title: 'Confirmation',
      content: (
        <div>
          Updating the <b>{toggleName}</b> will result in the previously uploaded file(s) to be deleted from the field{' '}
          <b>{fileUploadFieldName}</b> upon saving this form.
          <br />
          <br />
          Are you sure you want to update <b>{toggleName}</b>?
        </div>
      ),
      confirmButton: 'Yes',
      cancelButton: 'No'
    })
      .then(() => {
        fileUploadQuestions.forEach(question => {
          question.fileList.forEach(upload => {
            const { sitePatientIdentifier } = upload;
            const labelUniqueIdentifier = upload.attributeIdentifier;
            const fileUniqueIdentifier = upload.uniqueIdentifier;
            newSt.labelList.forEach(({ uniqueIdentifier, fileList }) => {
              if (uniqueIdentifier === labelUniqueIdentifier) {
                const fileWasDeletedFromList = !isEqual(
                  fileList,
                  remove(fileList, ({ uniqueIdentifier }) => uniqueIdentifier === fileUniqueIdentifier)
                );
                if (fileWasDeletedFromList) {
                  this.setState(({ deleteArray }) => ({
                    inputs: newSt,
                    previousInputs: cloneDeep(inputs),
                    deleteArray: uniq([
                      ...deleteArray,
                      {
                        sitePatientIdentifier,
                        fileUniqueIdentifier
                      }
                    ])
                  }));
                }
              }
            });
          });
          remove(question.fileList);
          this.finalizeProcessingRadioBtnSelection(radioVal, questionData, decodeVal);
        });
      })
      .catch(() => {
        const qIndex = findIndex(newSt.labelList, o => {
          return questionData.uniqueIdentifier === o.uniqueIdentifier;
        });
        let oldDecodeVal = undefined;
        let oldRadioVal = 0;
        if (questionData.inputValue === 'Y') {
          oldDecodeVal = 'Y';
          oldRadioVal = 1;
        } else if (questionData.inputValue === 'N') {
          oldDecodeVal = 'N';
          oldRadioVal = 2;
        }
        newSt.labelList[qIndex].inputValue = oldDecodeVal;
        this.setState(newSt);
        this.finalizeProcessingRadioBtnSelection(oldRadioVal, questionData, oldDecodeVal);
      });
  };

  finalizeProcessingRadioBtnSelection(radioVal, questionData, decodeVal) {
    try {
      const prevSt = this.state.inputs;
      const qIndex = findIndex(prevSt.labelList, o => {
        return questionData.uniqueIdentifier === o.uniqueIdentifier;
      });
      if (qIndex > -1) {
        const uid = prevSt.labelList[qIndex].uniqueIdentifier;
        prevSt.labelList[qIndex].inputValue = decodeVal;
        // setting question answers
        const prevSaveJson = this.state.jsonForSave;
        const checkForEle = findIndex(prevSaveJson, r => {
          return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
        });
        if (checkForEle > -1) {
          prevSaveJson[checkForEle].attributeValue = decodeVal; // this.getRadioCodeValue(prevSt.labelList[qIndex]["inputValue"]);
          prevSaveJson[checkForEle].comments = '';
          if (radioVal !== 0) {
            prevSaveJson[checkForEle].answerStatus = 1;
            prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
            prevSt.labelList[qIndex].ansStatus = PS_ANSWERED;
          } else {
            prevSaveJson[checkForEle].answerStatus = 0;
            prevSaveJson[checkForEle].performedStatus = PS_NOT_ANSWERED;
            prevSaveJson[checkForEle].ansComments = '';
            prevSt.labelList[qIndex].ansComments = '';
            prevSt.labelList[qIndex].ansStatus = PS_NOT_ANSWERED;
            prevSt.labelList[qIndex].attributeType = 'field';
            if (isLogAvailableForReviewCycle(this.state?.inputs?.domainCode)) {
              prevSaveJson[checkForEle].attributeValue = '';
              prevSt.labelList[qIndex].inputValue = '';
            }
          }
        } else {
          const qinfo = getSaveJSONInfo();
          qinfo.attributeValue = decodeVal;
          qinfo.performedStatus = PS_ANSWERED;
          qinfo.answerStatus = 1;
          qinfo.elementEncounterIdentifier = uid;
          qinfo.comments = '';
          qinfo.attributeType = 'field';
          qinfo.tableview = questionData.isTable ? 1 : 0;
          prevSaveJson.push(qinfo);
        }
        this.clearPreviousAnswersForHiddenQuestions(prevSt, qIndex, decodeVal);
        this.setState(
          {
            jsonForSave: prevSaveJson,
            inputs: prevSt
          },
          () => {
            // validate for the Edit check
            this.resetConditionalQuestion(questionData.uniqueIdentifier, decodeVal);
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  }

  clearPreviousAnswersForHiddenQuestions(prevSt, qIndex, decodeValue) {
    const { labelList } = prevSt;
    const conditionalQuestion = labelList[qIndex];
    const { uniqueIdentifier } = conditionalQuestion;
    const { formValidationRules } = this.state;
    if (fieldAffectsOnConditionalLogic(formValidationRules, uniqueIdentifier)) {
      const hiddenQuestions = getHiddenTargetFieldIdsByTriggerFieldValue(
        formValidationRules,
        uniqueIdentifier,
        decodeValue
      );
      labelList.forEach((e, index) => {
        if (hiddenQuestions.includes(e.uniqueIdentifier)) {
          labelList[index].ansComments = '';
        }
      });
    }
  }

  resetConditionalQuestion(triggerFieldId, triggerFieldValue) {
    const { formValidationRules, inputs, jsonForSave } = this.state;
    if (!fieldAffectsOnConditionalLogic(formValidationRules, triggerFieldId)) {
      return;
    }

    const groupedFieldIds = recursivelyCollectVisibleAndHiddenFieldIdsByTriggerField(
      formValidationRules,
      triggerFieldId,
      triggerFieldValue
    );

    forEach(inputs?.labelList, function(label) {
      retainOldStatus(label);
      recalculateVisibility(label, jsonForSave, groupedFieldIds);
    });

    this.setState({
      inputs: inputs,
      jsonForSave: jsonForSave
    });
  }

  onchangeTimeHandler = (moment, formLabelId, oid, questionData) => {
    try {
      const prevSt = this.state.inputs;
      const qIndex = findIndex(prevSt.labelList, o => {
        return questionData.uniqueIdentifier === o.uniqueIdentifier;
      });
      if (qIndex > -1) {
        const uid = prevSt.labelList[qIndex].uniqueIdentifier;
        prevSt.labelList[qIndex].inputValue = moment ? Common.getTimefromDate(moment._d, 24) : '';
        if (!prevSt.labelList[qIndex]['inputValue']) {
          prevSt.labelList[qIndex]['ansStatus'] = PS_NOT_ANSWERED;
        }
        // setting question answers
        const prevSaveJson = this.state.jsonForSave;
        const checkForEle = findIndex(prevSaveJson, r => {
          return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
        });
        if (checkForEle > -1) {
          prevSaveJson[checkForEle].attributeValue = moment ? Common.getTimefromDate(moment._d, 24) : '';

          prevSaveJson[checkForEle].comments = '';
          prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
          prevSaveJson[checkForEle].attributeType = 'field';
        } else {
          const qinfo = getSaveJSONInfo();
          qinfo.attributeValue = moment ? Common.getTimefromDate(moment._d, 24) : '';
          qinfo.performedStatus = PS_ANSWERED;
          qinfo.answerStatus = 1;
          qinfo.elementEncounterIdentifier = uid;
          qinfo.comments = '';
          qinfo.tableview = questionData.isTable ? 1 : 0;
          qinfo.attributeType = 'field';
          prevSaveJson.push(qinfo);
        }
        this.setState({ jsonForSave: prevSaveJson, inputs: prevSt });
      }
    } catch (e) {}
  };

  whenWasDoneDateOnChange = e => {
    this.setState({ fillDate: Common.formatDate(e._d, yearMonthDayFormat) }, () => {
      this.whenAndWhoDidItDefault.whenWasItDone = this.state.fillDate;
    });
  };

  getEmptyItemGroupList() {
    const jsonForSaveCopy = cloneDeep(this.state.jsonForSave);
    this.processAndHideConditionQuestions(jsonForSaveCopy);
    const labelList = this.state.inputs.labelList ? this.state.inputs.labelList : [];
    return jsonForSaveCopy.filter(filed => {
      return labelList.find(item => {
        if (this.excludeQues.includes(item.inputType)) {
          return false;
        }
        return this.isInputTypeValidation(item, filed);
      });
    });
  }

  isInputTypeValidation = (item, field) => {
    switch (item.inputType) {
      case 'file': {
        const isEmptyValue = !item.inputValue || item.inputValue.split(',').every(fileName => fileName === '');
        return (
          item.uniqueIdentifier === field.elementEncounterIdentifier &&
          field.show &&
          ((isEmptyValue && !item.fileList) || (isEmptyValue && item.fileList && item.fileList.length === 0))
        );
      }
      case 'singleFile': {
        const isEmptyValue = !item.inputValue || item.inputValue.split(',').every(fileName => fileName === '');
        return (
          item.uniqueIdentifier === field.elementEncounterIdentifier &&
          field.show &&
          ((isEmptyValue && !item.fileList) || (isEmptyValue && item.fileList && item.fileList.length === 0))
        );
      }
      default:
        return (
          item.uniqueIdentifier === field.elementEncounterIdentifier &&
          field.show &&
          (!item.inputValue || item.inputValue === '' || item.inputValue === '--')
        );
    }
  };

  investigatorSign = () => {
    const signData = {
      studyId: this.state.metadata.studyId || this.state.studyId,
      ssuPatientId: this.props.params.ssuPatientId,
      itemGroupTemplateId: this.state?.fullResponse?.formDefinition?.itemGroupKey,
      itemGroupTemplateVersionId: this.props.params.itemGroupTemplateVersionId,
      rowId: this.state.currentLogRowId
    };
    ModalBoxes.open({
      component: (
        <InvestigatorFieldsSignatureModal
          signData={signData}
          domainCode={this.state?.fullResponse?.formDefinition?.domainCode}
          groupAssignList={this.state.groupAssignList}
          callback={() => {
            this.redirectBackIfLogsNewRowAdded();
            if (this.state.patientInfoLogPage) {
              const params = {
                studyId: this.state.metadata.studyId,
                ssuPatient: this.props.params.ssuPatientId,
                encounterId: this.props.params.patientEncounterId,
                itemGroupTemplateVersionId:
                  this.state.patientInfoLogPage || this.state.encounterLogPage
                    ? this.props.params.itemGroupTemplateVersionId
                    : this.state.metadata.itemGroupTemplateVersionId,
                rowId: this.state.currentLogRowId
              };
              this.getLogRow(params).then(res => {
                this.loadLogRowLastStatus(
                  this.state.metadata.studyId || this.state.studyId || res.data.studyId,
                  this.props.params.ssuPatientId,
                  res.data.formDefinition.itemGroupKey,
                  this.props.params.itemGroupTemplateVersionId,
                  this.state.currentLogRowId
                );
              });
            }
          }}
        />
      ),
      className: 'eds-investigator-fields-sign-modal-box'
    });
  };

  pushToInvestigator = () => {
    LogRowStatusHistoryApi.pushToInvestigator(
      this.state.metadata.studyId || this.state.studyId,
      this.props.params.ssuPatientId,
      this.state?.fullResponse?.formDefinition?.itemGroupKey,
      this.props.params.itemGroupTemplateVersionId,
      this.state.currentLogRowId
    ).then(() => {
      if (this.state.patientInfoLogPage) {
        const params = {
          studyId: this.state.metadata.studyId,
          ssuPatient: this.props.params.ssuPatientId,
          encounterId: this.props.params.patientEncounterId,
          itemGroupTemplateVersionId:
            this.state.patientInfoLogPage || this.state.encounterLogPage
              ? this.props.params.itemGroupTemplateVersionId
              : this.state.metadata.itemGroupTemplateVersionId,
          rowId: this.state.currentLogRowId
        };
        this.getLogRow(params).then(res => {
          this.loadLogRowLastStatus(
            this.state.metadata.studyId || this.state.studyId || res.data.studyId,
            this.props.params.ssuPatientId,
            res.data.formDefinition.itemGroupKey,
            this.props.params.itemGroupTemplateVersionId,
            this.state.currentLogRowId
          );
          NotificationManager.success('Pushed To Investigator successfully.');
          this.redirectBackIfLogsNewRowAdded();
        });
      } else {
        NotificationManager.success('Pushed To Investigator successfully.');
        this.redirectBackIfLogsNewRowAdded();
      }
    }, onRequestError);
  };

  onSmConfirm = () => {
    LogRowStatusHistoryApi.smConfirm(
      this.state.metadata.studyId || this.state.studyId,
      this.props.params.ssuPatientId,
      this.state?.fullResponse?.formDefinition?.itemGroupKey,
      this.props.params.itemGroupTemplateVersionId,
      this.state.currentLogRowId
    ).then(() => {
      this.callBack = null;

      if (this.state.patientInfoLogPage) {
        const params = {
          studyId: this.state.metadata.studyId,
          ssuPatient: this.props.params.ssuPatientId,
          encounterId: this.props.params.patientEncounterId,
          itemGroupTemplateVersionId:
            this.state.patientInfoLogPage || this.state.encounterLogPage
              ? this.props.params.itemGroupTemplateVersionId
              : this.state.metadata.itemGroupTemplateVersionId,
          rowId: this.state.currentLogRowId
        };
        this.getLogRow(params).then(res => {
          this.loadLogRowLastStatus(
            this.state.metadata.studyId || this.state.studyId || res.data.studyId,
            this.props.params.ssuPatientId,
            res.data.formDefinition.itemGroupKey,
            this.props.params.itemGroupTemplateVersionId,
            this.state.currentLogRowId
          );
          NotificationManager.success('SM Check successful.');
          this.props.navigate(generateUrlByKey(this.props.currentRoute.parent.key, this.props.params));
        });
      } else {
        NotificationManager.success('SM Check successful.');
        this.props.navigate(generateUrlByKey(this.props.currentRoute.parent.key, this.props.params));
      }
    }, onRequestError);
  };

  isAnyQuestionAnswerChanged = () => {
    const isQuestionsDataChanged = this.isQuestionsDataChanged();
    const isFileDataChanged = this.isFileDataChanged();
    const isCommentChanged = this.state.additionNotes !== this.state.originalAdditionNotes;
    return isQuestionsDataChanged || isFileDataChanged || isCommentChanged;
  };

  isQuestionsDataChanged = () => {
    const jsonFieldAttributes = cloneDeep(this.state.jsonForSave)
      .filter(e => !isReadOnlyField(e.attributeType))
      .map(e => {
        return {
          elementEncounterIdentifier: e.elementEncounterIdentifier,
          value: e.attributeValue ? e.attributeValue : ''
        };
      });
    const originalJsonFieldAttributes = cloneDeep(this.state.originalJsonForSave)
      .filter(e => !isReadOnlyField(e.attributeType))
      .map(e => {
        return {
          elementEncounterIdentifier: e.elementEncounterIdentifier,
          value: e.attributeValue ? e.attributeValue : ''
        };
      });
    return !isEqual(jsonFieldAttributes, originalJsonFieldAttributes);
  };

  isFileDataChanged = () => {
    const jsonFiles = cloneDeep(this.state.jsonForSave)
      .filter(e => ['file', 'singleFile'].includes(e.attributeType))
      .map(e => {
        return {
          elementEncounterIdentifier: e.elementEncounterIdentifier,
          fileList: e?.fileList && !isEmpty(e.fileList) ? e.fileList : []
        };
      });

    return jsonFiles.some(e => !isEmpty(e.fileList)) || !isEmpty(this.state.deleteArray);
  };

  onPushToInvestigator = () => {
    this.onSaveData(this.pushToInvestigator, true);
  };

  openSaveDataModal = async redirectToAddNewRecord => {
    if (redirectToAddNewRecord) {
      this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl = `${
        this.props.currentRoute.pathname
      }?timestamp=${+new Date()}`;
    }
    const definition = this.state.fullResponse?.formDefinition;
    if (
      isReviewableLogType(definition?.domainCode, definition?.elementIcon) &&
      !this.state?.logRowStatus?.piStatus &&
      userHasRole([ROLE_STUDY_MANAGER, ROLE_SYSTEM_ADMINISTRATOR])
    ) {
      ModalBoxes.confirm({
        content: (
          <span>{`Is this ${resolveDomainFullName(
            definition?.domainCode
          ).toLowerCase()} ready to be pushed to the investigator?`}</span>
        ),
        cancelButton: 'No',
        confirmButton: 'Yes',
        size: 'w800'
      }).then(
        () => {
          this.onPushToInvestigator();
        },
        () => {
          this.onSaveData(() => {
            this.state?.fullResponse?.formDefinition?.elementIcon === 'Logs' && this.redirectBackIfLogsNewRowAdded();
          }, true);
        }
      );
    } else {
      await this.onSaveData(() => {
        this.state?.fullResponse?.formDefinition?.elementIcon === 'Logs' && this.redirectBackIfLogsNewRowAdded();
      }, true);
    }
  };

  onSaveData = async (callBack, useGeneralFlowWithCallback = false) => {
    this.callBack = callBack;
    this.useGeneralFlowWithCallback = useGeneralFlowWithCallback;
    if (this.validateDateAndWhoDidItFields()) {
      this.setState({ validationError: true });
      NotificationManager.error(SELECT_REQUIRED);
      return;
    }
    if (
      this.isDomainContainConditionalQuestions() &&
      isShownNotFilledConditionsExist(this.state.jsonForSave, this.state.inputs, this.state.formValidationRules)
    ) {
      !this.state.validationError && this.setState({ validationError: true });
      NotificationManager.error(SELECT_REQUIRED);
      return;
    }
    if (this.state.validationError) {
      this.setState({ validationError: false });
    }

    const emptyItemGroupList = filtrateByInvestigatorFieldIfNeed(
      this.getEmptyItemGroupList(),
      this.state.fullResponse.formDefinition
    );
    const notAllQuestionIsFilled = emptyItemGroupList.length > 0;
    if (notAllQuestionIsFilled && this.isStipendItemGroup()) {
      const missingFieldsIds = emptyItemGroupList.map(item => item.elementEncounterIdentifier);
      const fields = this.state.inputs.labelList
        .filter(label => missingFieldsIds.includes(label.uniqueIdentifier))
        .map(item => item?.updatedQuestion || item.question)
        .join(', ');
      NotificationManager.error(SELECT_REQUIRED_STIPEND + fields);
      return;
    }

    const jsonForSaveCopy = cloneDeep(this.state.jsonForSave);

    const isSomeCertifiedCopyIsNotConfirmed = jsonForSaveCopy
      .filter(attribute => FILE_TYPES.includes(attribute.attributeType) && attribute.hasOwnProperty('fileList'))
      .flatMap(attribute => attribute.fileList)
      .filter(file => file.isCertifyCopy)
      .some(file => !file.isCertifyCopyConfirmed);

    if (isSomeCertifiedCopyIsNotConfirmed) {
      this.setState({
        validation: {
          ...this.state.validation,
          isSomeCertifyCopyWasNotConfirmedAfterSaving: true
        }
      });
      NotificationManager.error(CHECKBOX_EXACT_COPY);
      return;
    }

    const name = find(this.state.whoCdashList, r => r.uniqueIdentifier === this.state.whoDidItUniqueIdentifier)?.name;
    if (name && name.includes('(Inactive)')) {
      try {
        await ModalBoxes.confirm({
          content:
            'The name appearing in the Who did it? field does not currently have an active role with this study. Are you sure you want to save these updates?',
          confirmButton: 'Save',
          cancelButton: 'Cancel'
        });
      } catch (e) {
        return;
      }
    }

    // in case of old dirty data, lets reset selectedPatientStatus that is maintained in state
    this.setState({ dropStatusDetails: {}, isRequireAttentionOriginalValues: {} });

    // grab selected patient status from user selected drop down
    const selectedPatientStatusId = await this.getSelectedPatientStatusWithId().then(status => status?.id);
    if (
      this.isPatientStatusChangeItemGroup() &&
      selectedPatientStatusId !== this.state.savedPatientStatus?.dispositionStatusId
    ) {
      // if drop status was selected, this will wait for user to
      // submit a Withdraw Reason in modal window before continuing.
      const response = await this.savePatientStatusChange();
      if (response === formActions.CANCEL) {
        // if user canceled from Withraw Reason modal then simply return
        // else we can continue with execution of saving data
        return;
      }
    }
    const itemGroupContainsCertifyCopyFiles = this.isItemGroupWithCertifyCopyFiles();
    if (notAllQuestionIsFilled && !this.isStipendItemGroup() && !itemGroupContainsCertifyCopyFiles) {
      this.recordedDataChanged(jsonForSave => {
        if (!isFunction(callBack) || this.useGeneralFlowWithCallback) {
          this.openMissingItemDialog(jsonForSave);
        } else {
          this.prepareAndAutoSaveDataWithMissedItems(jsonForSave);
        }
      });
      return;
    }
    if (itemGroupContainsCertifyCopyFiles) {
      this.saveItemGroupDataWithCertifyCopyFiles(notAllQuestionIsFilled);
      return;
    }
    this.recordedDataChanged(this.processSaving);
  };

  prepareAndAutoSaveDataWithMissedItems = jsonForSave => {
    const emptyList = this.toFormEmptyList();
    const tableview = this.state.inputs.columnList !== null && this.state.inputs.columnList.length > 0 ? 1 : 0;
    this.setTheAdditionalComments(newDataForSave(jsonForSave, emptyList, tableview));
  };

  savePatientStatusChange = async () => {
    if (this.isDropStatusSelected()) {
      return await this.openReasonCodeForWithdrawStatus();
    } else {
      return await this.getSelectedPatientStatusWithId().then(status => this.updatePatientStatus(status));
    }
  };

  isItemGroupWithCertifyCopyFiles() {
    const jsonForSaveCopy = cloneDeep(this.state.jsonForSave);
    return findAllUploadedFilesFromAllFileUploadQuestions(jsonForSaveCopy).some(
      uploadedFile => uploadedFile?.isCertifyCopy
    );
  }

  getStatusFieldValue() {
    const labelList = this.state?.inputs?.labelList || [];
    const patientStatusLabel = labelList.find(label => label?.cdashAliasName === 'STATUS');
    const patientStatusLabelId = patientStatusLabel?.uniqueIdentifier;
    if (patientStatusLabelId) {
      const patientStatusField = this.state.jsonForSave.find(
        field => field?.elementEncounterIdentifier === patientStatusLabelId
      );
      return patientStatusField?.attributeValue;
    }
    return null;
  }

  inAvailableStatuses = status => {
    return this.state.inputs.labelList[0].codeDefinationList.map(answer => answer.name).includes(status.name);
  };

  openReasonCodeForWithdrawStatus() {
    return new Promise(resolve => {
      const patientStatusesChangeModal = ModalBoxes.open({
        component: (
          <PatientStatusChangeModal
            initialStatus={this.getSelectedPatientStatus()}
            allowedStatusesPredicate={this.inAvailableStatuses}
            updateStatus={(status, reason, comment) => {
              this.setState(
                {
                  dropStatusDetails: {
                    withdrawReason: reason?.id,
                    withdrawComment: comment
                  }
                },
                () => {
                  patientStatusesChangeModal.close();
                  return resolve(formActions.SUBMIT);
                }
              );
            }}
          />
        ),
        onClose: () => resolve(formActions.CANCEL)
      });
    });
  }

  updatePatientStatus = (status, reason, comment) => {
    this.setState({
      dropStatusDetails: {
        withdrawReason: reason?.id,
        withdrawComment: comment
      }
    });
  };

  getSelectedPatientStatus() {
    const selectedValue = this.getStatusFieldValue();
    if (selectedValue && selectedValue !== 'No Change') {
      return { name: selectedValue };
    } else {
      return undefined;
    }
  }

  getSelectedPatientStatusWithId() {
    if (this.getSelectedPatientStatus()) {
      return SitePatientApi.getAllStatuses().then(({ data: allStatuses }) => {
        return allStatuses.find(s => s.name === this.getSelectedPatientStatus().name);
      });
    } else {
      return Promise.resolve();
    }
  }

  recordedDataChanged = nextFunc => {
    const { inputs, originalLabelData, jsonForSave } = this.state;
    const changedData = findQuestionsWithChangedAnswers(inputs, originalLabelData);

    if (!isEmpty(changedData)) {
      this.openItemGroupChangedDataModal(jsonForSave, changedData, nextFunc);
    } else {
      nextFunc.call(this, jsonForSave);
    }
  };

  saveItemGroupDataWithCertifyCopyFiles(notAllQuestionIsFilled) {
    const { inputs, originalLabelData, jsonForSave } = this.state;
    const questionsWithChangedAnswers = findQuestionsWithChangedAnswers(inputs, originalLabelData);
    if (!isEmpty(questionsWithChangedAnswers) && notAllQuestionIsFilled) {
      this.openItemGroupChangedDataModal(
        jsonForSave,
        questionsWithChangedAnswers,
        this.openItemGroupMissingItemModal.bind(this, this.openItemGroupCertifyCopySignModal)
      );
    } else if (!isEmpty(questionsWithChangedAnswers) && !notAllQuestionIsFilled) {
      this.openItemGroupChangedDataModal(
        jsonForSave,
        questionsWithChangedAnswers,
        this.openItemGroupCertifyCopySignModal
      );
    } else if (isEmpty(questionsWithChangedAnswers) && notAllQuestionIsFilled) {
      this.openItemGroupMissingItemModal(this.openItemGroupCertifyCopySignModal, jsonForSave);
    } else {
      this.openItemGroupCertifyCopySignModal(jsonForSave);
    }
  }

  openItemGroupChangedDataModal(jsonForSave, changedData, nextFunc) {
    const itemGroupChangedDataModal = ModalBoxes.open({
      component: (
        <ReasonForDataChangeModal
          onSave={jsonForSave => {
            itemGroupChangedDataModal.close();
            nextFunc.call(this, jsonForSave);
          }}
          onClose={() => this.handleCallbackOnFinishSave(false, true)}
          jsonForSave={cloneDeep(jsonForSave)}
          changedData={changedData}
        />
      )
    });
  }

  openItemGroupMissingItemModal(nextFunc, jsonForSave) {
    const jsonForSaveCopy = jsonForSave.filter(e =>
      checkOnInvestigatorFieldIfNeeded(e, this.state.fullResponse.formDefinition.domainCode)
    );
    ModalBoxes.open({
      component: (
        <MissingItemDialog
          emptyItemGroupList={this.toFormEmptyList()}
          actualFormData={this.state.inputs}
          forSaveForm={jsonForSaveCopy}
          onSave={upd => {
            nextFunc.call(
              this,
              jsonForSave.map(e => {
                const exsisting = upd.find(el => el.elementEncounterIdentifier === e.elementEncounterIdentifier);
                return exsisting || e;
              })
            );
          }}
          onClose={() => this.handleCallbackOnFinishSave(false)}
        />
      ),
      title: `${this.toFormEmptyList()?.length > 1 ? 'Missing Items' : 'Missing Item'}`
    });
  }

  openItemGroupCertifyCopySignModal(jsonForSave) {
    ModalBoxes.open({
      component: (
        <CertifyCopySignModal
          onSave={jsonForSave => {
            this.setTheAdditionalComments(jsonForSave);
          }}
          jsonForSave={jsonForSave}
        />
      )
    });
  }

  processSaving(jsonForSave) {
    // Separate flow for comment item group
    if (this.state.inputs && this.state.inputs.hasOwnProperty('comments')) {
      return this.updatePatientEncounterKeyInCommentFiledForCommentItemGroup(jsonForSave);
    } else {
      return this.setTheAdditionalComments(jsonForSave);
    }
  }

  isPatientStatusChangeItemGroup() {
    const patientStatusChangeDomainCodes = ['EPS', 'ECR', 'EDO'];
    const isPatientStatusChangeDomainCode = patientStatusChangeDomainCodes.includes(this.state?.inputs?.domainCode);
    const labelList = this.state?.inputs?.labelList || [];
    const isCdashAliasNameStatus = labelList.some(label => label?.cdashAliasName === 'STATUS');
    return isPatientStatusChangeDomainCode && isCdashAliasNameStatus;
  }

  isEpsItemGroup() {
    return this.state?.inputs?.domainCode === 'EPS';
  }

  isStipendItemGroup() {
    return this.state.inputs.domainCode === 'EPSTP';
  }

  validateDateAndWhoDidItFields = () => {
    // When was it done? - filled
    // Who did it? - filled
    return !this.state.fillDate || isEmpty(this.state.whoDidItUniqueIdentifier);
  };

  openMissingItemDialog(jsonForSave) {
    const jsonForSaveCopy = jsonForSave.filter(e =>
      checkOnInvestigatorFieldIfNeeded(e, this.state.fullResponse.formDefinition.domainCode)
    );
    ModalBoxes.open({
      component: (
        <MissingItemDialog
          emptyItemGroupList={this.toFormEmptyList().filter(e =>
            checkOnInvestigatorFieldIfNeeded(e, this.state.fullResponse.formDefinition.domainCode)
          )}
          actualFormData={this.state.inputs}
          forSaveForm={jsonForSaveCopy}
          onSave={upd =>
            this.setTheAdditionalComments(
              jsonForSave.map(e => {
                const exsisting = upd.find(el => el.elementEncounterIdentifier === e.elementEncounterIdentifier);
                return exsisting || e;
              })
            )
          }
          onClose={() => this.handleCallbackOnModalClose()}
        />
      ),
      className: 'eds-missing-item-dialog',
      title: `${this.toFormEmptyList()?.length > 1 ? 'Missing Items' : 'Missing Item'}`
    });
  }

  toFormEmptyList() {
    return orderBy(
      this.getEmptyItemGroupList().map(({ elementEncounterIdentifier, comments, performedStatus }) => {
        const field = find(this.state.inputs.labelList, ['uniqueIdentifier', elementEncounterIdentifier]);
        return {
          investigatorField: field.investigatorField,
          name: field.updatedQuestion ? field.updatedQuestion : field.question,
          comments,
          performedStatus,
          elementEncounterIdentifier,
          inputType: field.inputType,
          sequence: field.sequence
        };
      }),
      [user => user.sequence]
    );
  }

  /**
   * I wanted to reuse "setTheAdditionalComments", but it looks for 'COVAL' form filed.
   * When update 'Comment' item group data 'COVAL' filed is not in 'jsonForSave' property
   */
  updatePatientEncounterKeyInCommentFiledForCommentItemGroup(jsonForSave) {
    const additionNote = {
      answerStatus: 1,
      attributeValue: this.state.additionNotes,
      comments: this.state.additionNotes,
      elementEncounterIdentifier: 'COVAL',
      meausrementvalue: '',
      performedStatus:
        this.state.additionNotes && this.state.additionNotes.trim().length > 0 ? PS_ANSWERED : PS_NOT_ANSWERED,
      tableview: 0
    };
    if (
      jsonForSave &&
      jsonForSave.length > 0 &&
      jsonForSave[0].hasOwnProperty('patientEncounterKey') &&
      jsonForSave[0].patientEncounterKey
    ) {
      additionNote.patientEncounterKey = jsonForSave[0].patientEncounterKey;
    }

    let patientEncounterFormIdentifier = this.props.params.patientItemGroupId;
    if (this.state.patientInfoLogPage === true) {
      patientEncounterFormIdentifier = '';
    }
    const obj = {
      patientEncounterFormGroupIdentifier: this.state.patientEncounterFormGroupIdentifier,
      patientEncounterFormIdentifier: patientEncounterFormIdentifier,
      formDomainMasterUniqueIdentifier:
        this.state.metadata.itemGroupTemplateVersionId || this.state.formDomainMasterUniqueIdentifier,
      whoDiditInCdash: this.state.whoDidItUniqueIdentifier,
      whenDiditInCdash: this.state.fillDate,
      attributeList: [additionNote],
      hasTableType: this.state.hasTableType,
      tableTitle: this.state.editedRowType, // TODO: this property and state variable can be removed after small fix
      studyId: this.state.metadata.studyId || this.state.studyId,
      ssuPatientId: this.props.params.ssuPatientId,
      itemGroupTemplateId: 'ce3665fc-021a-49e9-ac83-d16b2a051111',
      itemGroupTemplateVersionId: 'CMELb08b9c15d16d11e894874f3b94111111',
      rowId: jsonForSave.filter(x => x.patientEncounterKey !== null)[0]?.rowId
    };
    return this.saveAllChangesForElement(obj);
  }

  processAndHideConditionQuestions(questions) {
    const { formValidationRules } = this.state;
    const hiddenFieldIds = calculateHiddenFieldIds(questions, formValidationRules);

    questions.forEach(question => {
      if (hiddenFieldIds.includes(question.elementEncounterIdentifier)) {
        question.performedStatus = PS_UNAVAILABLE; // Unavailable
        question.show = false;
      }
    });
  }

  resolveAnswersIfNeed(jsonForSave) {
    if (
      (this.state.currentLogRowId === 'newRow' || this.state.encounter.notStarted) &&
      !userHasRole(ROLE_PRINCIPAL_INVESTIGATOR) &&
      !userHasRole(ROLE_SYSTEM_ADMINISTRATOR) &&
      isInvestigatorFieldAvailable(this.state.fullResponse.domainCode)
    ) {
      return jsonForSave.map(e => {
        return e.investigatorField && e.show ? { ...e, performedStatus: PS_NOT_ANSWERED } : e;
      });
    }

    if (
      isInvestigatorFieldAvailable(this.state.fullResponse.domainCode) &&
      (userHasRole(ROLE_PRINCIPAL_INVESTIGATOR) || userHasRole(ROLE_SYSTEM_ADMINISTRATOR))
    ) {
      return jsonForSave.map(e => {
        if (this.excludeQues.includes(e.attributeType) && e.show) {
          e.performedStatus = PS_ANSWERED;
        }
        return e;
      });
    }
    return jsonForSave;
  }

  setTheAdditionalComments = prevSvJson => {
    const data = this.resolveAnswersIfNeed(prevSvJson);
    const additionNote = {
      answerStatus: 1,
      attributeValue: this.state.additionNotes,
      comments: this.state.additionNotes,
      elementEncounterIdentifier: 'COVAL',
      meausrementvalue: '',
      performedStatus:
        this.state.additionNotes && this.state.additionNotes.trim().length > 0 ? PS_ANSWERED : PS_NOT_ANSWERED,
      tableview: 0
    };

    if (this.state.inputs && this.state.inputs.columnList && this.state.inputs.columnList.length > 0) {
      additionNote.tableview = 1;
    }

    const covalIdx = findIndex(data, u => {
      return u.elementEncounterIdentifier === 'COVAL';
    });

    if (covalIdx > -1) {
      data[covalIdx].attributeValue = this.state.additionNotes;
      data[covalIdx].comments = this.state.additionNotes;
      data[covalIdx].performedStatus =
        this.state.additionNotes && this.state.additionNotes.trim().length > 0 ? PS_ANSWERED : PS_NOT_ANSWERED;
    } else {
      isArray(data) && data.push(additionNote);
    }
    this.processAndHideConditionQuestions(data);

    this.setState({ jsonForSave: data }, () => {
      let patientEncounterFormIdentifier = this.props.params.patientItemGroupId;
      if (this.state.patientInfoLogPage === true) {
        patientEncounterFormIdentifier = '';
      }
      const obj = {
        patientEncounterFormGroupIdentifier: this.state.patientEncounterFormGroupIdentifier,
        patientEncounterFormIdentifier: patientEncounterFormIdentifier,
        formDomainMasterUniqueIdentifier:
          this.state.metadata.itemGroupTemplateVersionId || this.state.formDomainMasterUniqueIdentifier,
        whoDiditInCdash: this.state.whoDidItUniqueIdentifier,
        whenDiditInCdash: this.state.fillDate,
        attributeList: this.state.jsonForSave,
        hasTableType: this.state.hasTableType,
        tableTitle: this.state.editedRowType,
        studyId: this.state.metadata.studyId || this.state.studyId,
        ssuPatientId: this.props.params.ssuPatientId,
        itemGroupTemplateId: this.state?.fullResponse?.formDefinition?.itemGroupKey,
        itemGroupTemplateVersionId:
          this.state.patientInfoLogPage || this.state.encounterLogPage
            ? this.props.params.itemGroupTemplateVersionId
            : this.state.metadata.itemGroupTemplateVersionId,
        itemGroupDomain: this.state.inputs.domainCode
      };

      if (this.state.inputs.elementIcon === 'Logs') {
        obj.rowId = this.state.currentLogRowId === 'newRow' ? null : this.state.currentLogRowId;
      } else {
        obj.rowId = data.filter(x => x.patientEncounterKey !== null)[0]?.rowId;
      }

      if (this.state.encounterLogPage || this.state.patientInfoLogPage) {
        obj.associatedEncounterId = this.state.selectedEncounterId;
      }

      const { inputs, originalLabelData } = this.state;
      const changedData = findQuestionsWithChangedAnswers(inputs, originalLabelData);

      const isLogCheckPerformedOption =
        !this.state.patientInfoLogPage &&
        this.state.encounterLogPage &&
        (this.props.currentRoute.params.rowId === 'newRow' || !isEmpty(changedData));
      return this.saveAllChangesForElement(obj, isLogCheckPerformedOption);
    });
  };

  fillWithAnEmptyAnswers(prevSvJson) {
    if (this.state.inputs && this.state.inputs.labelList && this.state.inputs.labelList.length > 0) {
      this.state.inputs.labelList.forEach(lblData => {
        // elementEncounterIdentifier
        const hasDt = prevSvJson.filter(r => {
          return r.elementEncounterIdentifier === lblData.uniqueIdentifier;
        });
        if (hasDt.length <= 0) {
          const rTmpObj = {
            investigatorField: lblData.investigatorField,
            answerStatus: 1,
            attributeValue: '',
            comments: '',
            elementEncounterIdentifier: lblData.uniqueIdentifier,
            meausrementvalue: '',
            performedStatus: PS_ANSWERED,
            tableview: this.state.inputs?.columnList?.length > 0 ? 1 : 0
          };
          isArray(prevSvJson) && prevSvJson.push(rTmpObj);
        }
      });
    }
  }

  whoDidItOnChange = (option, predefined = false) => {
    if (!option || !option.uniqueIdentifier || this.state.whoDidItUniqueIdentifier === option.uniqueIdentifier) {
      return;
    }
    this.setState(
      { whoDidItUniqueIdentifier: option.uniqueIdentifier, whoDidItUniqueIdentifierPredefined: predefined },
      () => {
        this.whenAndWhoDidItDefault.whoDidItUniqueIdentifier = this.state.whoDidItUniqueIdentifier;
      }
    );
  };

  encounterOnChange = option => {
    this.setState({ selectedEncounterId: option?.id });
  };

  // saving form changes
  saveAllChangesForElement(data, isLogCheckPerformedOption) {
    LoadingOverlay.showLoader();
    const epochIdentifier = this.state.metadata.encounterType === UNEXPECTED ? 'fromUnexpectedEncounter' : 'false';
    const elementFormIdenfier = get(this, 'props.params.patientItemGroupId');
    data.attributeList = this.reformatMultiselectAttributeValueIfArray(data.attributeList);

    const promises = [];
    if (epochIdentifier === 'fromUnexpectedEncounter' && elementFormIdenfier === 'null') {
      return;
    } else {
      const { deleteArray } = this.state;
      if (isArray(deleteArray) && !isEmpty(deleteArray)) {
        deleteArray.forEach(item => {
          promises.push(UserMediaApi.deleteMedia(item.sitePatientIdentifier, item.fileUniqueIdentifier));
        });
      }
    }
    data.dropStatusDetails = {
      dropStatusReasonId: this.state.dropStatusDetails.withdrawReason,
      statusChangeComment: this.state.dropStatusDetails.withdrawComment
    };

    if (this.state.encounter.domainCode === 'ICF') {
      populateIcfAnswers(data, this.state.inputs.labelList, this.props?.informedConsentForm);
    }

    const formData = new FormData();
    Object.values(this.state.filesToUpload)
      .flatMap(filesWithIds => Object.entries(filesWithIds).map(([fileId, file]) => ({ [fileId]: file })))
      .forEach(fileWithKey => {
        const [[key, value]] = Object.entries(fileWithKey);
        formData.append(key, value);
      });

    formData.append('body', new Blob([JSON.stringify(data)], { type: 'application/json' }));

    return Promise.all(promises).then(
      () => {
        let apiCall;
        if (isLogCheckPerformedOption) {
          apiCall = NewEncounterLogCheckApi.saveLogCheckReported(
            this.props.currentRoute.params.ssuPatientId,
            this.props.currentRoute.params.patientEncounterId,
            this.props.currentRoute.params.patientItemGroupId,
            this.state.metadata.ssuId,
            formData
          );
        } else {
          apiCall = StudyApi.saveItemGroupWithRows(data, formData);
        }
        apiCall
          .then(
            res => {
              flushSync(() => {
                this.setState(
                  {
                    currentLogRowId: res?.data?.rowId,
                    originalWhoDidItUniqueIdentifier: this.state.whoDidItUniqueIdentifier,
                    whoDidItUniqueIdentifierPredefined: false,
                    originalLabelData: this.state.inputs.labelList
                  },
                  () => {
                    LoadingOverlay.hideLoader();
                    NotificationManager.success(DATA_SAVED);
                    if (this.state.inputs?.elementName) {
                      this.blockedNavigation();
                    }
                    if (
                      this.useGeneralFlowWithCallback &&
                      this.state?.fullResponse?.formDefinition?.elementIcon === 'Logs'
                    ) {
                      this.redirectBackIfLogsNewRowAdded();
                    }
                  }
                );
              });
            },
            err => {
              LoadingOverlay.hideLoader();
              onRequestError(err, { messageLocation: 'response.data.message' });
            }
          )
          .finally(() => {
            ModalBoxes.closeAll();
            if (!isEmpty(this.state.inputs?.columnList)) {
              UserFormTable.resetFormView();
            }
            if (has(this.state.inputs, 'comments') && has(this.state.inputs, 'listOfComments')) {
              CommentTemplate.resetFormView();
            }
            if (this.ref.current) {
              this.loadFormDataSetGeneral().then(() => {
                this.addShowAndConditionalFlags();
                this.handleCallbackOnFinishSave();
              });
            } else {
              const availableForReviewCycle =
                isLogAvailableForReviewCycle(this.state?.fullResponse?.formDefinition?.domainCode) &&
                this.state?.fullResponse?.formDefinition?.elementIcon === 'Logs';
              this.handleCallbackOnFinishSave(!availableForReviewCycle);
            }
          });
      },
      () => {
        LoadingOverlay.hideLoader();
      }
    );
  }

  reformatMultiselectAttributeValueIfArray(attributeList) {
    return attributeList.map(attribute => {
      if (this.isMultiselectInputType(attribute.attributeType)) {
        let parsedAttributeValue = null;

        try {
          parsedAttributeValue = JSON.parse(attribute.attributeValue);
        } catch {
          // swallow exception; parsedAttributeValue remains null
        }

        if (isArray(parsedAttributeValue)) {
          attribute.attributeValue = attribute.originalValue;
        }
      }
      return attribute;
    });
  }

  isMultiselectInputType(attributeType) {
    return attributeType === 'multiselect' || attributeType === 'multiselectCT';
  }

  redirectBackIfLogsNewRowAdded = () => {
    if (this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl) {
      this.props.navigate(this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl);
      return;
    }
    if (
      isLogAvailableForReviewCycle(this.state?.fullResponse?.formDefinition?.domainCode) &&
      this.state?.fullResponse?.formDefinition?.elementIcon === 'Logs'
    ) {
      this.props.navigate(
        generateUrlByKey(this.props.currentRoute.key, {
          ...this.props.params,
          rowId: this.state.currentLogRowId
        }),
        { replace: true }
      );
    } else {
      this.props.navigate(generateUrlByKey(this.props.currentRoute.parent.key, this.props.params));
    }
  };

  onRowClickHandler = formGroupIdentifier => {
    try {
      const forthcomingState = {};
      const { allResponseData } = this.state,
        parsedResponseData = allResponseData && allResponseData.length && JSON.parse(allResponseData),
        currentItemDefinition = this.getCurrentItemDefinition(parsedResponseData, formGroupIdentifier);

      if (!isEmpty(currentItemDefinition)) {
        const currentFormValidation = prepareFormValidationRules(currentItemDefinition),
          currentFormData = currentItemDefinition.formData,
          currentFileList = currentItemDefinition.fileList,
          currentFormDefinition = currentItemDefinition.formDefinition,
          currentFormDataRecord = getFormDataRecordByFormGroupIdentifier(currentFormData, formGroupIdentifier);

        if (!isEmpty(currentFormDataRecord)) {
          const jsonForSave = toArray(currentFormDataRecord.pateintEncounterFieldResponse),
            tableTypeItemGroupRowName = get(
              currentFormDataRecord,
              'pateintEncounterFieldResponse.9999999.attributeValue'
            ),
            { whenDidIt, whoDidItUniqueIdentifier, additionNotes } = getWhenAndWhoDidItFromFormDataRecord(
              currentFormDataRecord
            );

          currentFormDefinition.labelList = prepareFormDefinitionLabelList(currentFormDefinition.labelList);

          const labelList = prepareFormDefinitionLabelListWithFilledAnswersForQuestions(
            currentFormDefinition.labelList,
            currentFormDataRecord,
            currentFileList
          );

          if (!isEmpty(labelList)) {
            currentFormDefinition.labelList = labelList;
          }

          const defaultJsonForSave = generateJsonForSaveByFormDefinition(
            currentFormDefinition.labelList,
            currentFormDefinition.columnList
          );

          assign(forthcomingState, {
            patientEncounterFormGroupIdentifier: formGroupIdentifier,
            jsonForSave: unionBy(jsonForSave, defaultJsonForSave, 'elementEncounterIdentifier')
          });

          if (this.state.hasTableType && tableTypeItemGroupRowName) {
            assign(forthcomingState, { editedRowType: tableTypeItemGroupRowName });
          }

          if (!isEmpty(whenDidIt)) {
            assign(forthcomingState, {
              fillDate: whenDidIt,
              originalFillDate: whenDidIt
            });
          }

          if (!isEmpty(whoDidItUniqueIdentifier)) {
            assign(forthcomingState, {
              whoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
              originalWhoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
              whoDidItUniqueIdentifierPredefined: false
            });
          }

          assign(forthcomingState, {
            inputs: currentFormDefinition,
            originalLabelData: getInputsByFormDefinitionLabelList(currentFormDefinition.labelList),
            formValidationRules: currentFormValidation,
            additionNotes: additionNotes || '',
            originalAdditionNotes: additionNotes || '',
            hasTableView: false
          });
          this.setState(forthcomingState, () => {
            this.addShowAndConditionalFlags();
          });
        }
      } else {
        this.resetRowFrmId();
      }
    } catch (e) {
      console.log(e);
    }
  };

  // adding fresh form for table
  resetRowFrmId = () => {
    try {
      const { allResponseData, formValidationRules } = this.state;
      const parsedResponseData = allResponseData && allResponseData.length && JSON.parse(allResponseData);
      const currentItemDefinition = this.getCurrentItemDefinition(parsedResponseData);

      const newFormValidationRules = prepareFormValidationRules(currentItemDefinition) || formValidationRules || '';

      const currentFormDefinition = currentItemDefinition.formDefinition;

      currentFormDefinition.labelList = prepareFormDefinitionLabelList(currentFormDefinition.labelList);

      const jsonForSave = generateJsonForSaveByFormDefinition(
        currentFormDefinition.labelList,
        currentFormDefinition.columnList
      );

      this.setState(
        {
          inputs: currentFormDefinition,
          originalLabelData: getInputsByFormDefinitionLabelList(currentFormDefinition.labelList),
          patientEncounterFormGroupIdentifier: '',
          jsonForSave: jsonForSave,
          hasTableView: false,
          additionNotes: '',
          originalAdditionNotes: '',
          formValidationRules: newFormValidationRules
        },
        () => this.addShowAndConditionalFlags()
      );
    } catch (e) {
      console.log(e);
      this.setState({ hasTableView: false });
    }
  };

  resetStateForTable = () => {
    this.setState({
      hasTableView: true,
      jsonForSave: [],
      fillDate: this.whenAndWhoDidItDefault.whenWasItDone,
      whoDidItUniqueIdentifier: this.whenAndWhoDidItDefault.whoDidItUniqueIdentifier,
      originalFillDate: this.whenAndWhoDidItDefault.whenWasItDone,
      originalWhoDidItUniqueIdentifier: this.whenAndWhoDidItDefault.whoDidItUniqueIdentifier,
      whoDidItUniqueIdentifierPredefined: false,
      deleteArray: [],
      areFilesAttached: false
    });
  };

  cancelTableForm = callBack => {
    if (this.isUnsavedData()) {
      ModalBoxes.confirm({
        content: <div>Are you sure you want to leave this page without saving your edits?</div>,
        confirmButton: 'Yes',
        cancelButton: 'No'
      }).then(
        () => {
          this.resetStateForTable();
          isFunction(callBack) && callBack();
        },
        () => {}
      );
    } else {
      this.resetStateForTable();
      isFunction(callBack) && callBack();
    }
  };

  onchangeCheckboxHandler = (e, questionData) => {
    try {
      const prevSt = this.state.inputs;
      const qIndex = findIndex(prevSt.labelList, o => {
        return questionData.uniqueIdentifier === o.uniqueIdentifier;
      });
      if (qIndex > -1) {
        const uid = prevSt.labelList[qIndex].uniqueIdentifier;
        prevSt.labelList[qIndex].inputValue = e;
        if (!prevSt.labelList[qIndex].inputValue) {
          prevSt.labelList[qIndex].ansStatus = PS_NOT_ANSWERED;
        }
        const prevSaveJson = this.state.jsonForSave;
        const checkForEle = findIndex(prevSaveJson, r => {
          return r.elementEncounterIdentifier === prevSt.labelList[qIndex].uniqueIdentifier;
        });
        if (checkForEle > -1) {
          prevSaveJson[checkForEle].attributeValue = e;
          prevSaveJson[checkForEle].comments = '';
          prevSaveJson[checkForEle].performedStatus = PS_ANSWERED;
        } else {
          const qinfo = getSaveJSONInfo();
          qinfo.attributeValue = e;
          qinfo.performedStatus = PS_ANSWERED;
          qinfo.answerStatus = 1;
          qinfo.elementEncounterIdentifier = uid;
          qinfo.comments = '';
          qinfo.tableview = questionData.isTable ? 1 : 0;
          prevSaveJson.push(qinfo);
        }
        this.setState({ jsonForSave: prevSaveJson, inputs: prevSt });
      }
    } catch (e) {}
  };

  onTblCovalChangeHandeler = e => {
    this.setState({ additionNotes: e.target.value });
  };

  isDomainContainConditionalQuestions() {
    return (
      this.state.inputs && this.state.inputs.domainCode && EDIT_CHECK_DOMAINS.indexOf(this.state.inputs.domainCode) > -1
    );
  }

  addNewComment = () => {
    this.setState({
      additionNotes: '',
      originalAdditionNotes: '',
      hasTableView: false,
      patientEncounterFormGroupIdentifier: '',
      jsonForSave: []
    });
  };

  editAditionalNotes = editData => {
    const formGroupIdentifier = editData.original.formGroupIdentifier,
      { allResponseData } = this.state,
      parsedResponseData = allResponseData && allResponseData.length && JSON.parse(allResponseData),
      currentItemDefinition = this.getCurrentItemDefinition(parsedResponseData, formGroupIdentifier),
      { formData } = currentItemDefinition;
    const { whenDidIt, whoDidItUniqueIdentifier } = getWhenAndWhoDidItByFormGroupIdentifier(
      formData,
      formGroupIdentifier
    );

    this.setState({
      additionNotes: editData.original.comments ? editData.original.comments : '',
      originalAdditionNotes: editData.original.comments ? editData.original.comments : '',
      patientEncounterFormGroupIdentifier: formGroupIdentifier,
      jsonForSave: [editData.original],
      fillDate: whenDidIt,
      originalFillDate: whenDidIt,
      whoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
      originalWhoDidItUniqueIdentifier: whoDidItUniqueIdentifier,
      whoDidItUniqueIdentifierPredefined: false
    });
  };

  getCurrentItemDefinition = (data, formGroupIdentifier) => {
    const { patientEncounterGroupId: patientEncounterGroupIdentifier } = this.state.metadata;
    return (
      data &&
      isArray(data.itemDefination) &&
      data.itemDefination.find(({ encounter, formData }) => {
        if (isString(formGroupIdentifier) && !isEmpty(formGroupIdentifier)) {
          return formData.some(({ fromGroupIdentifier }) => fromGroupIdentifier === formGroupIdentifier);
        }
        return encounter.patientEncounterGroupIdentifier === patientEncounterGroupIdentifier;
      })
    );
  };

  getSavedPatientStatus = () => {
    const ssuPatientId = this.props.params?.ssuPatientId;
    if (this.isPatientStatusChangeItemGroup() && ssuPatientId) {
      StudyApi.getPatientRelatedStudies(ssuPatientId).then(({ data = [] }) => {
        const match = data.find(val => val?.sitePatientIdentifier === ssuPatientId);
        this.setState({ savedPatientStatus: { dispositionStatusId: match?.dispositionStatusId } });
      }, onRequestError);
    }
  };

  render() {
    const { state } = this,
      { patientInfo, allResponseData, inputs: formData, hasTableType } = state,
      itemGroupTableData = hasTableType ? prepareItemGroupTableData(allResponseData) : null,
      currentDomainCode = get(formData, 'domainCode');

    const headerProps = {
      validationError: this.state.validationError,
      formData: formData,
      isAbleToSave: this.isAbleToSave,
      isAbleToSign: this.isAbleToSign,
      showEncounterSelect:
        (this.state.encounterLogPage || this.state.patientInfoLogPage) && showEncounterColumn(currentDomainCode),
      encounterOnChange: this.encounterOnChange,
      selectedEncounterId: state.selectedEncounterId,
      checkedInEncountersForPatient: state.checkedInEncountersForPatient,
      whenWasDoneDate: state.fillDate,
      whenWasDoneDateOnChange: this.whenWasDoneDateOnChange,
      whoDidItList: state.whoCdashList,
      whoDidItOnChange: this.whoDidItOnChange,
      whoDidItUniqueIdentifier: state.whoDidItUniqueIdentifier,
      onSaveData: this.openSaveDataModal,
      onInvestigatorSign: this.investigatorSign,
      onPushToInvestigator: this.onPushToInvestigator,
      onSmConfirm: this.onSmConfirm,
      logRowStatus: this.state.logRowStatus,
      rowId: this.state.currentLogRowId,
      elementIcon: this.state.inputs.elementIcon,
      isDetailRowOpen: this.state.isDetailRowOpen,
      inheritedComponent: true,
      isAnyQuestionAnswerChanged: () =>
        isReviewableLogType(
          this.state.fullResponse.formDefinition.domainCode,
          this.state.fullResponse.formDefinition.elementIcon
        ) && this.isAnyQuestionAnswerChanged(),
      noSkippedQuestions: !this.state.jsonForSave.filter(e => e.show).some(e => e.performedStatus === PS_NOT_ANSWERED),
      informedConsentForm: this.props.informedConsentForm
    };

    const notNewReviewableLogType = isNotNewReviewableLogType(
      this.props.currentRoute.params.rowId,
      this.state.inputs.domainCode,
      this.state.inputs.elementIcon
    );

    return (
      <div className={cx('get-domain-detail-elements', { disabled: !this.isAbleToEdit() })} ref={this.ref}>
        {!this.props.inheritedComponent && (
          <EncounterPatientInfo
            infoLabel={formData && formData.hasOwnProperty('comments') ? 'Comment Logs' : formData.elementName}
            withLabel={!isEmpty(formData) && !isReviewableLogType(formData.domainCode, formData.elementIcon)}
          />
        )}
        <section className="border">
          <div className="eds-item-group-header">
            <div className="eds-item-group-header-left">
              {this.state.logRowStatus &&
                (isCurrentPageForLogWithAvailableReviewCycle(this.props.currentRoute.name) ||
                  (this.props.currentRoute.name === 'Row' &&
                    this.props.currentRoute?.parent?.parent?.name !== 'Encounter Details')) && (
                  <ItemGroupBar
                    className="item-group-bar-review"
                    name={this.state.inputs?.elementName}
                    statusChip={resolveSmStatus(
                      this.state.logRowStatus.smStatus,
                      isNull(this.state.logRowStatus?.piStatus)
                    )}
                    historyLink={
                      isLogAvailableForReviewCycle(currentDomainCode) &&
                      generateUrlByKey(`${this.props.currentRoute.key}.History`, this.props.currentRoute.params)
                    }
                  >
                    {notNewReviewableLogType && (
                      <LogRowCloseReopen
                        logRowStatus={this.state.logRowStatus}
                        studyId={this.state.inputs.studyIdentifier}
                        itemGroupTemplateId={this.state?.fullResponse?.formDefinition?.itemGroupKey}
                        elementName={this.state.inputs.elementName}
                        currentDomainCode={currentDomainCode}
                      />
                    )}
                  </ItemGroupBar>
                )}
            </div>
            <div className="eds-item-group-header-right">
              {['Logs', 'ICF'].includes(formData.elementIcon) && (
                <Stack direction="row" spacing={1}>
                  {notNewReviewableLogType &&
                    isLogCanBeLinked(currentDomainCode) &&
                    userHasAccessTo(MANAGE_LINKED_LOGS) &&
                    userCanSignOrEdit(this.state.currentUserPermissionType) && (
                      <LinkLogButton
                        itemGroupTemplateId={this.state?.fullResponse?.formDefinition?.itemGroupKey}
                        itemGroupTemplateVersionId={this.props.currentRoute.params?.itemGroupTemplateVersionId}
                        studyId={this.state.studyId}
                        rowId={this.props.currentRoute.params?.rowId}
                        patientInfo={patientInfo}
                        loadLinkedSubjects={true}
                        onCloseModalCallback={() => {
                          this.loadLogRowLastStatus(
                            this.state.studyId,
                            this.props.params.ssuPatientId,
                            this.state?.fullResponse?.formDefinition?.itemGroupKey,
                            this.props.params.itemGroupTemplateVersionId,
                            this.state.currentLogRowId
                          );
                          this.props.navigate(
                            generateUrlByKey(this.props.currentRoute.key, {
                              ...this.props.params,
                              rowId: this.state.currentLogRowId
                            }),
                            { replace: true }
                          );
                        }}
                        label="LINKED PD"
                      />
                    )}
                  {formData.elementIcon === 'Logs' && <LogRecordsNavigation />}
                  <IconButton
                    aria-label="Cancel"
                    title="Cancel"
                    className="cancel-button"
                    color="primary"
                    disabled={isCancelRowStatus(this.state.logRowStatus)}
                    onClick={() => {
                      this.props.navigate &&
                        this.props.navigate(
                          generateUrlByKey(this.props.currentRoute.parent.key, this.props.currentRoute.params)
                        );
                    }}
                  >
                    <CloseIcon data-testid="CloseIcon" />
                  </IconButton>
                </Stack>
              )}
            </div>
          </div>
          <div className="eds-item-group-body">
            {formData && formData.hasOwnProperty('comments') ? (
              <CommentTemplate
                editAditionalNotes={this.editAditionalNotes}
                addNewComment={this.addNewComment}
                cancelTableForm={this.cancelTableForm}
                onTblCovalChangeHandeler={this.onTblCovalChangeHandeler}
                additionNotes={this.state.additionNotes}
                patientEncounterId={this.state.metadata?.patientEncounterGroupId}
                formData={formData}
                isAllowedToManage={this.isAllowedToManageLogs}
                openDetailRow={this.openDetailRow}
                closeDetailRow={this.closeDetailRow}
                headerProps={headerProps}
              />
            ) : (
              <UserFormTable
                patientInfo={patientInfo}
                onChangeTextHandler={this.onChangeTextHandler}
                onChangeDropDownHandler={this.onChangeDropDownHandler}
                onDateChangeHandler={this.onDateChangeHandler}
                onChangeRadioHandler={this.onChangeRadioHandler}
                onchangeTimeHandler={this.onchangeTimeHandler}
                onChangeFileHandler={this.onChangeFileHandler}
                deleteFile={this.deleteFile}
                deleteFileForSingleFileUpload={this.deleteFileForSingleFileUpload}
                onCloseSelectedFile={this.onCloseSelectedFile}
                formData={formData}
                itemGroupTableData={itemGroupTableData}
                onRowClickHandeler={this.onRowClickHandler}
                resetRowFrmId={this.resetRowFrmId}
                filledQuestionsAns={this.state.jsonForSave}
                cancelTableForm={this.cancelTableForm}
                onchangeCheckboxHandler={this.onchangeCheckboxHandler}
                onTblCovalChangeHandeler={this.onTblCovalChangeHandeler}
                additionNotes={this.state.additionNotes}
                formValidationRules={this.state.formValidationRules}
                hasTableType={this.state.hasTableType}
                editedRowType={this.state.editedRowType}
                formGroupIdentifier={this.state.patientEncounterFormGroupIdentifier}
                onChangeRequireAttention={this.onChangeRequireAttention}
                isAbleToEdit={this.isAbleToEdit}
                validation={this.state.validation}
                openDetailRow={this.openDetailRow}
                closeDetailRow={this.closeDetailRow}
                ssuPatientId={this.props.params.ssuPatientId}
                validationError={this.state.validationError}
                headerProps={headerProps}
                logRowStatus={this.state.logRowStatus}
                groupAssignPermissionType={this.props.selectedItemGroup?.permissionType}
                filesToUploadGeneralSize={this.state.filesToUploadGeneralSize}
              />
            )}
          </div>
        </section>
      </div>
    );
  }

  openDetailRow = () => {
    this.setState({
      isDetailRowOpen: true
    });
  };

  closeDetailRow = () => {
    this.setState({
      isDetailRowOpen: false
    });
  };

  isAbleToSave = () => {
    if (!this.isAbleToEdit()) {
      return false;
    }
    if (this.isEpsItemGroup()) {
      return !this.itemGroupIsCompleted() && !userHasRole([userRoles.ROLE_STUDY_LEAD]);
    }
    const { hasTableView } = this.state;
    return !hasTableView;
  };

  isAbleToEdit = () => {
    if (
      isNotNewReviewableLogType(
        this.props.currentRoute.params.rowId,
        this.state.inputs.domainCode,
        this.state.inputs.elementIcon
      ) &&
      isCancelRowStatus(this.state.logRowStatus)
    ) {
      return false;
    }
    const { inputs, currentUserPermissionType } = this.state;

    if ([ALLOW_READ, DENY_ALL].includes(currentUserPermissionType)) {
      return false;
    }

    if (this.state?.encounter?.domainCode === 'EPS' && this.state?.encounter?.completed) {
      return false;
    }
    if (this.state?.encounter?.domainCode === 'ICF') {
      return this.state.metadata?.encounterType === NON_PROTOCOL
        ? userHasRole(ALLOWED_TO_MANAGE_NON_PROTOCOL_ENCOUNTER_ROLES)
        : userHasRole(ALLOWED_TO_MANAGE_PATIENT_ENCOUNTER_ROLES);
    }
    if (this.state?.encounter?.notPerformed) {
      return false;
    }
    return inputs && (inputs.elementIcon === LOGS || inputs.hasOwnProperty('comments'))
      ? this.isAllowedToManageLogs()
      : this.isAllowedToManageEncounter();
  };

  isAbleToSign = () => {
    const { currentUserPermissionType } = this.state;
    return currentUserPermissionType ? currentUserPermissionType === ALLOW_SIGN : true;
  };

  isAllowedToManageLogs = () => {
    const { jsonForSave } = this.state;

    if (isArray(jsonForSave) && !isEmpty(jsonForSave)) {
      const { patientEncounterFormIdentifier } = jsonForSave[0];
      if (isString(patientEncounterFormIdentifier) && !isEmpty(patientEncounterFormIdentifier)) {
        if (patientEncounterFormIdentifier !== this.props.params.patientItemGroupId) {
          return false;
        }
      }
    }
    return (
      userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
      (this.state.patientInfo?.studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_LOG_DATA))
    );
  };

  isAllowedToManageEncounter = () => {
    return this.state.metadata?.encounterType === NON_PROTOCOL
      ? userHasAccessTo(MANAGE_PRE_SCREEN_WORK_LIST)
      : userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
          (this.state.patientInfo?.studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_PROTOCOL_ENCOUNTER_DATA));
  };

  itemGroupIsCompleted() {
    return get(this.state, 'fullResponse.encounter.completed', false);
  }

  isDropStatusSelected() {
    const selectedPatientStatus = this.getSelectedPatientStatus();
    return ['Pre-screen Failed', 'Run In Failed', 'Screen Failed', 'Withdrawn', 'Early Termination'].includes(
      selectedPatientStatus?.name
    );
  }

  onChangeRequireAttention = (event, questionData) => {
    const { isRequireAttentionOriginalValues } = this.state;
    const { checked } = event.target;
    const data = this.state.jsonForSave;
    const savedDataItem = data.find(data => data.elementEncounterIdentifier === questionData.uniqueIdentifier);
    if (!(questionData.uniqueIdentifier in isRequireAttentionOriginalValues)) {
      isRequireAttentionOriginalValues[questionData.uniqueIdentifier] = savedDataItem.isRequireAttention;
    }
    savedDataItem.isRequireAttention = checked;
    this.setState({ jsonForSave: data, isRequireAttentionOriginalValues });
  };

  handleCallbackOnFinishSave = (update = true, clearInputs = false) => {
    clearInputs && this.setState({ inputs: cloneDeep(this.state.previousInputs) });
    if (isFunction(this.callBack)) {
      this.callBack();
      this.callBack = null;
      if (this.useGeneralFlowWithCallback) {
        if (this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl) {
          isFunction(this.props.onSubmit) && this.props.onSubmit(false);
        } else {
          isFunction(this.props.onSubmit) && this.props.onSubmit();
        }
      } else {
        isFunction(this.props.onSubmit) && this.props.onSubmit(false);
      }
    } else if (update) {
      if (this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl) {
        isFunction(this.props.onSubmit) && this.props.onSubmit(false);
      } else {
        isFunction(this.props.onSubmit) && this.props.onSubmit();
      }
    }
  };

  handleCallbackOnModalClose = () => {
    if (isFunction(this.callBack)) {
      this.callBack = null;
      if (this.useGeneralFlowWithCallback) {
        if (this.redirectBackIfLogsNewRowAdded.redirectToAddNewRecordUrl) {
          isFunction(this.props.onSubmit) && this.props.onSubmit(false);
        } else {
          isFunction(this.props.onSubmit) && this.props.onSubmit();
        }
      } else {
        isFunction(this.props.onSubmit) && this.props.onSubmit(false);
      }
    }
  };
}

function mergeFileLists(prevFileList, newFileList) {
  if (!isUndefined(prevFileList)) {
    newFileList.forEach(file => {
      const index = findIndex(prevFileList, r => {
        return r.content === file.content && r.name === file.name;
      });
      if (index > -1) {
        prevFileList[index] = file;
      } else {
        prevFileList.push(file);
      }
    });
  }

  return prevFileList;
}

GetDomainDetailElements.defaultProps = {
  inheritedComponent: false
};

export default withCurrentRoute(withNavigate(withParams(GetDomainDetailElements)));
